import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import numeral from 'numeral';
import moment from 'moment';
import { Link } from 'react-router-dom';
import {
  Popover,
  Tag,
  Divider,
  Popconfirm,
  message as antMessage,
  Button
} from 'antd';
import {
  CheckOutlined,
  DownloadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import {
  routes,
  subRoutes,
  outOfNavRoutes
} from '../../utils/constants/routes';
import {
  exemptionStatusColor,
  tasksStatuses,
  ticketsPrioritiesColor
} from '../../utils/constants/tagColors';
import useErrorMessage from '../../utils/ErrorMessage';
import useAuthContext from '../../contexts/AuthContext';
import ExemptionModificationModal from '../tickets/ExemptionModificationModal';
import GuaranteesModal from './GuaranteesModal';
import { activityTypeLabelMap } from '../quotes/rcpmkFields';
import {
  RCPHX_WORLDWIDE_ALLOWED_ACTIVITIES,
  RCPHX_CYBER_ALLOWED_ACTIVITIES,
  activityRCPHXTypeLabelMap,
  getActivityPrerequisites,
  getInsuranceHistoricLabel,
  getRCPHXDeductibleAmountRCE
} from '../quotes/rcphxFields';

import { arrayConditionnalElement } from '../../utils/arrayConditionnalElement';
import { isWKMri, isWKPno } from '../quotes/mriwkFields';

const useListContent = (contract, type) => {
  const {
    number,
    status,
    total_monthly_customer,
    commission_fee,
    broker_fee,
    paperwork_fee,
    assurmax_paperwork_fee,
    paperwork_fee_payment,
    customer,
    guarantee_limit,
    deductible_amount,
    employees_number,
    representatives_number,
    yearly_events,
    participants_per_event,
    total_members,
    fraction_type,
    local,
    legal_protection,
    civil_responsibility_asso,
    local_insurance,
    extended_legal_protection,
    exploitation_responsibility,
    social_mandate_responsibility,
    number_claims,
    payment_type,
    termination_by_company,
    termination_reason_by_company,
    termination_date_by_company,
    turnover,
    created_at,
    surface_area,
    activities,
    company_in_creation,
    is_in_liquidation_or_receivership,
    straw_rubber_activity,
    industrial_activity,
    claims,
    insurer,
    quote_number,
    bank_name,
    bank_address,
    bank_postal_code,
    bank_city,
    bic,
    iban,
    rib_not_subscriber,
    effective_date,
    termination_date,
    termination_reason,
    termination_comment,
    email_sending,
    remuneration,
    previous_contract,
    building_type,
    building_type_other,
    concrete_floor,
    absence_concrete_floor,
    empty_unoccupied_works,
    guarantee_type,
    deductible_cost_index,
    empty_reason,
    machine_breakdown,
    indoor_pool,
    outdoor_pool,
    solar_panels,
    veranda,
    occupation_trade,
    activities_trade,
    risk_been_insured,
    company_been_insured,
    buildings,
    main_use,
    specific_risk,
    building_category,
    building_condition,
    construction_year,
    acquisition_date,
    is_continuously_insured_since_acquisition,
    total_buildings,
    risk_typology,
    risk_typology_other,
    rc_volunteer_union,
    outdoor_indoor_pool,
    renewable_energies,
    last_company_name,
    last_contract_number,
    last_termination_date,
    subscriber_type,
    subscriber_type_other,
    '75percent_tough_materials': tough_materials,
    '90percent_tough_materials': percent_tough_materials,
    half_timbering_structure,
    industrial_straw_activity,
    construction_activity,
    localized_qpv,
    is_qpv,
    contractual_indemnity_limit,
    medical_activity,
    legal_activity,
    has_already_been_terminated_by_asx,
    france_with_corsica,
    additional_costs,
    VTAM_choc_provisioire,
    operation_of_premises,
    intoxication_alimentaire,
    neighboring_third_party_recourses,
    rc_pro_guarantee,
    business_loss_guarantee,
    market_value,
    '2_incidents_max': incidents_max,
    '28m_high': m_high,
    take_care_building,
    more_500m_altitude,
    close_help,
    castle,
    monument,
    liquidation,
    ffb_1000_times,
    closed_more_60_days,
    mall,
    embassy_bowling_disco,
    religious_political,
    industry_less_10m,
    wood_plastic_nuclear_activity,
    straw_rubber_stock,
    disaffected,
    walking_cooler,
    renovation,
    extinguisher_conformed,
    sea_side,
    combustible_packaging,
    wholesale_business,
    tags_roof_materials,
    risk_has_graffitis_or_tags,
    roof_non_standard_materials,
    roof_built_or_replaced_after_2018,
    roof_is_flat_or_terrace,
    electric_conformed,
    restaurant_activity,
    close_before_2300,
    metropolitan_france,
    religious_pol,
    litigation_responsibility,
    blocked_activities,
    zone_ind_com_art,
    built_covered_hard_materials,
    annual_budget,
    political_activity,
    max_claims,
    no_claims,
    confirm_claims,
    is_a_palace,
    had_waived_certain_remedies_in_its_lease,
    chilled_products_conformed,
    fire_explosion_attack_climatic_event,
    attack,
    catastrophe,
    tech_catastrophe,
    theft_vandalism,
    light_inflatable_structure,
    water_damage,
    broken_windows,
    broken_machine,
    electric_damage,
    external_furniture,
    chilled_merchandise,
    archives_capital,
    value_capital,
    art_capital,
    theft_capital,
    object_of_value,
    owner_insurance,
    exploitation_loss,
    tenant_responsibility,
    neighbours_appeal,
    tenant_appeal,
    lawsuit_defense,
    lent_goods,
    legal_protection_mrp,
    extended_legal_protection_mrp,
    assistance,
    reinforced_accident_insurance,
    classic_accident_insurance,
    rc_pro,
    death,
    disability,
    lawsuit,
    package: pckg,
    capital,
    hard_materials,
    good_state,
    altitude_location,
    accessibility,
    activity_open_public,
    contractual_general_indemnity_limit,
    castle_manor,
    classified_listed,
    redress_liquidation,
    wooden_construction,
    temporary_activity,
    specific1_activities,
    religious_political_union,
    close_industrial_complex,
    specific2_activities,
    specific3_activities,
    waiver_recourse_against_tenants,
    cold_rooms_flammable_liquids,
    land_classified_non_constructible,
    building_height,
    agricultural_building,
    exemption,
    furniture_capital,
    furniture_capital_other,
    termination_asking_date,
    particular_clause,
    sites,

    //  DO
    max_tax_result,
    more_than_2_years,
    no_merge_takeover,
    stock_exchange_listing,
    insolvency_proceeding,
    not_operate_in_forbidden_activities,
    positive_last_2_tax_result,
    positive_last_2_equity,
    last_5_years_blame,
    employer_protection,
    cyber_protection,
    holding_protection,
    key_person_protection,

    // RCPMK
    is_startup,
    activity_type,
    service_activity_details,
    operational_protection,
    cyber_events_protection,
    key_man_protection,
    merge_protection,
    management_protection,
    it_forbidden_activities,
    it_regulated_activities,
    media_forbidden_activities,
    service_forbidden_activities_one,
    service_forbidden_activities_two,
    service_forbidden_activities_three,
    consulting_forbidden_activities,
    managment_protection_prerequisite,
    cyber_protection_prerequisite,
    merge_protection_prerequisite,
    etablishment_outside_france,
    limited_turnover_in_usa,
    limited_claims_indemnity,
    current_claim,
    // RCPML
    key_man_guarantee,
    mobility_guarantee,
    // RCPHX
    insurance_historic_hiscox,
    activity_detail,
    turnover_usa_canada,
    turnover_usa_canada_amount,
    turnover_usa_canada_amount_type,
    legal_protection_rcphx,
    legal_protection_rcphx_extended,
    legal_protection_usa_canada,
    history_risk_hiscox,
    cyber_option_rcphx,
    cyber_option_rcphx_extended,
    cyber_option_rcphx_fraud,

    // RCNGAWK
    guarantee_package,
    w_garage_certificate_number,
    w_garage_certificate,
    withdrawal_drivin_license,
    liquidation_property_or_legal_recovery,
    guaranteed_liability_involved,
    more_than_two_claims,
    compliant_installations,
    bodily_claim,
    driving_while_intoxicated,
    political_person_exposed,
    activity,
    max_fleet_size,
    vehicle_value_range,
    entrusted_vehicles,
    glass_breakage_guarantee,
    hail_guarantee,

    // MRIML
    risk_assured_location_outside_metropolitan_france,
    activity_in_qpv_area,
    risk_under_construction_or_90_percent_materials,
    risk_is_for_depollution_rehabilitation,
    risk_is_contiguous_with_industrial_activity,
    proximity_of_building_to_risk_of_peril_in_marseille,
    risk_is_wooden_structure_or_wood_cladding,
    risk_is_occupied_illegally,
    risk_is_subject_to_alignment,
    risk_contains_inflammable_liquids,
    risk_is_above_1500m_and_inaccessible_by_rescue,
    presence_of_lease_with_waiver_of_recourse_clause,
    building_subject_to_major_works_or_renovation,
    information_statements_submission_date,
    risk_dedicated_to_demolition,
    establishments_receiving_public_category_1_to_4,
    establishments_receiving_public_category_5_closing_before_23h,
    risk_involves_specific_high_risk_activities,
    risk_under_judicial_administration,
    roof_contains_non_standard_materials,
    contains_asbestos_or_fibrocement,
    policyholder_in_bankruptcy_or_liquidation,
    risk_is_vacant_land,
    risk_declared_contains_high_risk_activities_or_premises,
    risk_contains_religious_political_diplomatic_or_union_activity,
    risk_declared_is_high_rise_or_large_real_estate_complex,
    risk_declared_is_a_castle,
    risk_declared_is_classified_or_listed_building,
    risk_is_an_unbuildable_land,
    risk_contains_cold_storage_above_120m3,
    risk_declared_is_in_flood_zone,
    risk_is_not_in_good_condition,
    will_property_be_renovated_in_4_months,
    will_property_be_renovated_in_more_than_4_months,
    risk_declared_under_unsanitary_or_dangerous_decree,
    risk_includes_veranda_over_3m2,
    is_property_dealer,

    // pnoml
    property_under_construction,
    under_construction_duration,
    furnished_rental,
    squat_option,
    tenant_in_restructuring_or_liquidation,
    property_under_construction_prerequisite,
    no_prohibited_activities_declared,
    property_in_renovation_over_4_months,
    property_in_historic_building,
    property_in_castle_or_listed_building,
    risk_has_prohibited_restaurant_activities,
    risk_has_industrial_activities,
    is_property_occupied_or_will_be_within_2_months,
    property_previously_insured,
    have_you_owned_property_for_more_than_2_months,

    // rcgls
    main_activity,
    no_activity_usa_canada,
    not_listed_in_eu_sanctions,
    accurate_and_truthful_declarations,
    report_change_of_situation,
    no_acpr_sanctions,
    no_foreign_branch_or_subsidiary,
    no_problems_last_5_years,
    no_known_risk_facts,
    orias_required_for_guarantee,
    registration_required_for_guarantee,
    already_resiliated_by_assurmax,
    politically_exposed_person,
    legal_and_regulatory_compliance,
    // ACI prerequisites
    multiple_principals,
    registered_commercial_agent,
    professional_card_number,
    in_process_cci,
    issuing_chamber,
    agency_contact_details,
    // CGP_CIF prerequisites
    no_financial_engineering,
    real_estate_tax_reduction_activity,
    no_asset_management_for_third_parties,
    no_miscellaneous_goods_intermediation,
    no_gestion_patrimoine_without_cif,
    // IO BSP prerequisites
    mandataires_to_cover,
    orias_compliance_insurance,
    accessory_activity_ia_mia_dfb,
    accessory_activity_ia_mia,
    accessory_activity_dfb,

    // COA
    delegation_or_mandate,
    wants_financial_guarantee,
    financial_guarantee_amount,
    accounts_verified_by_accountant,
    mandataires_to_cover_mias,
    no_risky_insurance_contexts,
    reinsurance_broker,
    orias_compliance_assurance,
    accessory_activity_iobsp_dfb_cif,
    accessory_activity_iobsp,
    accessory_activity_cif,

    // AI
    no_foreign_branch,
    legal_representative_name,
    no_property_management_activity,
    real_estate_tax_reduction_activity_ai,
    wants_financial_guarantee_no_funds,
    financial_guarantee_amount_no_funds,
    accessory_activity_ia_mia_or_iobsp_miobsp,
    accessory_activity_ia_mia_alt,
    accessory_activity_iobsp_miobsp,

    // MIA
    single_mandate_insurance,
    orias_compliance_bank,
    risky_mandant_relationships,
    maritime_insurance,
    maritime_percentage,
    construction_insurance,
    construction_insurance_company_check,
    construction_project_check,
    construction_revenue_percentage,

    // MIOBSP
    single_mandate_iobsp,
    mandant_details,
    orias_compliance_miobsp,
    orias_number,
    only_france_based_clients,
    no_us_canada_bank_activity,

    accounts_verified_by_accountant_mia
  } = contract || {};

  const {
    priority,
    assignee,
    exemptions = [],
    file,
    desired_premium,
    unknown_desired_cotisation,
    web_site,
    comment,
    status: ticketStatus,
    _id
  } = exemption || {};
  const { t } = useTranslation();
  const { message } = useErrorMessage();
  const { dispatchAPI, user } = useAuthContext();
  const customerView = user?.role === 'guests:GUEST';
  const [openModal, setOpenModal] = useState(false);
  const [openFormuleModal, setOpenFormuleModal] = useState(false);
  const [activityDescriptionMap, setActivityDescriptionMap] = useState(
    new Map()
  );

  const getActivityData = async () => {
    try {
      const { data = [] } = await dispatchAPI('GET', {
        url: '/activities'
      });

      const activitiesWithDescription = data.filter(
        (activity) => !!activity.description
      );

      const descriptionMap = activitiesWithDescription.reduce(
        (acc, activity) => {
          acc.set(activity.name, activity.description);
          return acc;
        },
        new Map()
      );

      setActivityDescriptionMap(descriptionMap);
    } catch (e) {
      // DO NOTHING
    }
  };

  useEffect(() => {
    if (Array.isArray(activities) && activities.length > 0) {
      getActivityData();
    }
  }, [activities]);

  const MAIN_ACTIVITY_ENUM = {
    ACI: 'Agent Commercial Immobilier',
    CGP_CIF: 'Conseiller en Gestion de Patrimoine',
    IOBSP: 'Intermédiaire en Opérations de Banque',
    COA: 'Courtier d’Assurance',
    AI: 'Agent Immobilier',
    MIA: 'Mandataire d’Intermédiaire d’Assurance',
    MIOBSP:
      'Mandataire d’Intermédiaire en Opération de Banque et de Services de Paiement'
  };

  const downloadDocument = async (id, name, contentType) => {
    try {
      const response = await dispatchAPI('GET', {
        url: `/tickets/files/${_id}/${id}`,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: contentType
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      antMessage.success(t('export.messages.success'));
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const baseCostsInfo = [
    ...(!previous_contract
      ? [
          {
            label: `${status === 'QUOTE' ? 'quotes' : 'contracts'}.form.number`,
            content: status === 'QUOTE' ? quote_number : number,
            span: 1
          }
        ]
      : [
          {
            label: `${status === 'QUOTE' ? 'quotes' : 'contracts'}.form.number`,
            content:
              status === 'QUOTE'
                ? quote_number
                : previous_contract?.number?.split('_')[0],
            span: 1
          }
        ]),
    ...(exemption
      ? [
          {
            label: `quotes.form.exemption_status`,
            content: ticketStatus && (
              <Tag color={tasksStatuses[ticketStatus]}>
                {t(`tickets.status.${ticketStatus}`).toUpperCase()}
              </Tag>
            )
          }
        ]
      : []),

    {
      label: `${
        !previous_contract
          ? 'employees.contract_type'
          : 'employees.amendment_type'
      }`,
      content: type && t(`${type}.title`)
    },
    {
      label: 'contracts.form.previous_contract',
      content: previous_contract ? (
        <Link
          to={`${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CONTRACTS}/show/${previous_contract?._id}`}
        >
          {t(`quotes.form.switch.yes`)}
        </Link>
      ) : (
        t(`quotes.form.switch.no`)
      )
    },
    {
      label: 'contracts.column.created_at',
      content: created_at && moment(created_at).format('DD/MM/YYYY')
    },
    ...(status !== 'QUOTE'
      ? [
          {
            label: 'contracts.form.effective_date',
            content:
              effective_date &&
              moment(effective_date).utc().format('DD/MM/YYYY à HH:mm'),
            span: 2
          }
        ]
      : []),
    ...(type !== 'prevoyance' &&
    type !== 'association' &&
    ((customer && customer.company_name) ||
      (contract && contract.legal_entity_name))
      ? [
          {
            label: customer.company_name
              ? 'contracts.form.company_name'
              : 'contracts.form.legal_entity_name',
            content: customer.company_name
              ? customer.company_name
              : contract.legal_entity_name
          }
        ]
      : []),
    ...(type === 'association'
      ? [
          {
            label: 'contracts.form.association_name',
            content: customer.company_name
          }
        ]
      : []),
    ...(type !== 'prevoyance' &&
    type !== 'rcphx' &&
    type !== 'mri' &&
    type !== 'mriml' &&
    type !== 'pnoml'
      ? [
          {
            label: 'contracts.show.activities',
            content:
              activities &&
              activities.map((element) => {
                const activityName = element.split(' - ')[0].trim();
                let description = null;
                if (activityDescriptionMap.has(activityName)) {
                  description = activityDescriptionMap.get(activityName);
                }
                return (
                  <p key={element}>
                    {element}
                    {!!description && (
                      <span
                        style={{
                          fontSize: 11,
                          fontStyle: 'italic',
                          marginLeft: 2
                        }}
                      >
                        ({description})
                      </span>
                    )}
                  </p>
                );
              }),
            ellipsis: true
          }
        ]
      : []),
    {
      label: 'contracts.form.customer_name',
      content: customer && (
        <Link
          to={`${!customerView ? routes.CONTRACTS : ''}${
            !customerView ? subRoutes.CONTRACTS.PORTFOLIO_CLIENTS : ''
          }${customerView ? outOfNavRoutes.PROFILE : ''}${
            !customerView ? '/show/' : ''
          }${!customerView ? contract?._id : ''}`}
        >
          <span
            className={
              exemptions?.includes('tracfin') ? 'ant-tag ant-tag-red' : ''
            }
          >
            {`${customer.first_name} ${customer.last_name}`}
          </span>
        </Link>
      )
    },
    {
      label: 'contracts.show.customer_address',
      content:
        customer?.address &&
        `${customer.address.number} ${customer.address.street}, ${customer.address.postal_code} ${customer.address.city}`
    },
    ...(type === 'mri' ||
    type === 'prevoyance' ||
    type === 'mriml' ||
    type === 'pnoml'
      ? []
      : [
          {
            label:
              type === 'association'
                ? 'contracts.form.rna'
                : 'contracts.form.siret',
            content: customer && (
              <div>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://www.societe.com/cgi-bin/search?champs=${
                    customer && customer.siret
                  }`}
                >
                  {type === 'association'
                    ? customer && customer.rna
                    : customer && customer.siret}
                </a>
                <Popover
                  placement="right"
                  content={
                    <div className="siret-popover">
                      <div>
                        <span>{t('contracts.form.customer.status')}</span>
                        <span>
                          {t(`contracts.form.customer.${customer?.status}`)}
                        </span>
                      </div>
                      <div>
                        <span>
                          {type === 'association'
                            ? t(
                                'contracts.form.customer.association_creation_date'
                              )
                            : t(
                                'contracts.form.customer.company_creation_date'
                              )}
                        </span>
                        <span>
                          {customer &&
                            moment(customer.company_creation_date).format('L')}
                        </span>
                      </div>
                      <div>
                        <span>{t('contracts.form.customer.naf')}</span>
                        <span>
                          {customer && customer?.naf ? `${customer.naf}` : '-'}
                        </span>
                      </div>
                      <div>
                        <span>
                          {t('contracts.form.customer.principal_activity')}
                        </span>
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`https://www.coover.fr/code-ape-naf/${
                            customer.naf &&
                            customer.naf.replace(
                              new RegExp('[^(A-Z0-9)]', 'g'),
                              ''
                            )
                          }`}
                        >
                          Voici le lien
                        </a>
                      </div>
                      <div>
                        <span>{t('contracts.form.customer.INSEE_status')}</span>
                        <span>
                          {customer && customer.company_status
                            ? t(
                                `contracts.form.customer.${customer.company_status}`
                              )
                            : 'Aucune donnée'}
                        </span>
                      </div>
                    </div>
                  }
                >
                  <EyeOutlined />
                </Popover>
              </div>
            ),
            span: 1
          }
        ]),

    ...(particular_clause
      ? [
          {
            label: 'settings.tickets.form.particular_clause',
            content: particular_clause && particular_clause,
            span: 1
          }
        ]
      : []),
    ...(type !== 'mrp' || type !== 'association'
      ? []
      : [
          {
            label: 'contracts.form.sites',
            content: !!sites?.length && (
              <div>
                {sites.map((site) => (
                  <div>
                    <span>
                      {`• ${site.address?.number} ${site.address?.street} ${site.address?.city} 
                          `}
                    </span>
                  </div>
                ))}
              </div>
            )
          },
          {
            label: 'contracts.form.resident_status',
            content: !!sites?.length && (
              <div>
                {sites.map((site) => (
                  <div>
                    <span>
                      {`
                    ${t(`contracts.form.options.${site.resident_status}`)}
                          `}
                    </span>
                  </div>
                ))}
              </div>
            )
          },
          {
            label: 'contracts.form.surface_area_sites',
            content: !!sites?.length && (
              <div>
                {sites.map((site) => (
                  <div>
                    <span>{`${site.surface_area} m²`}</span>
                  </div>
                ))}
              </div>
            )
          },
          {
            label: 'contracts.show.deductible_amount',
            content: contract && (
              <div>{deductible_amount && `${deductible_amount} €`}</div>
            )
          },
          {
            label: 'contracts.form.employees_number',
            content: `${numeral(employees_number || 0).format('0,0')}`
          }
        ]),
    ...(type !== 'mrp' || type !== 'association'
      ? []
      : [
          {
            label: 'contracts.show.deductible_amount',
            content: contract && (
              <div>{deductible_amount && `${deductible_amount} €`}</div>
            )
          },
          {
            label: t('contracts.form.local'),
            content: t(`quotes.form.switch.${local ? 'yes' : 'no'}`),
            span: 1
          },

          {
            label: 'contracts.form.sites',
            content: sites && sites?.length && (
              <div>
                {sites.map((site) => (
                  <div>
                    <span>
                      {`• ${site.address?.number} ${site.address?.street} ${site.address?.city} 
                            `}
                    </span>
                  </div>
                ))}
              </div>
            )
          },
          {
            label: 'contracts.form.resident_status',
            content: sites && sites?.length && (
              <div>
                {sites.map((site) => (
                  <div>
                    <span>
                      {`
                      ${t(`contracts.form.options.${site.resident_status}`)}
                            `}
                    </span>
                  </div>
                ))}
              </div>
            )
          },
          {
            label: 'contracts.form.surface_area_sites',
            content: sites && sites?.length && (
              <div>
                {sites.map((site) => (
                  <div>
                    <span>{`${site.surface_area} m²`}</span>
                  </div>
                ))}
              </div>
            )
          },
          {
            label: 'contracts.form.turnover',
            content: turnover && `${numeral(turnover || 0).format('0,0')}`
          },
          {
            label: 'contracts.form.employees_number',
            content: `${numeral(employees_number || 0).format('0,0')}`
          }
        ])
  ];

  const contributionInfo = [
    {
      label: 'contracts.form.total_monthly',
      key: 'total_monthly_customer',
      content: `${(total_monthly_customer || 0).toFixed(2)}€`
    },
    {
      label: 'contracts.form.total_year',
      content: `${(total_monthly_customer * 12).toFixed(2)} € / an ${
        paperwork_fee
          ? `(${(total_monthly_customer * 12 + paperwork_fee).toFixed(
              2
            )} € la 1ère année)`
          : ''
      }`
    },
    ...(customerView
      ? []
      : [
          {
            label: 'contracts.form.commission_fee_list_display',
            key: 'commission_fee',
            content: `${commission_fee || 0}%`
          },
          {
            label: 'contracts.form.total_monthly_income',
            key: 'remuneration',
            content: `${(remuneration || 0).toFixed(2)}€`
          },
          {
            label: 'contracts.form.total_year_insurer',
            content: `${(remuneration * 12 || 0).toFixed(2)}€`
          },
          {
            label: `contracts.form.paperwork_fee${
              type === 'prevoyance' ? '_prev' : ''
            }`,
            content: `${(paperwork_fee || 0).toFixed(2)}€`
          },
          {
            label: 'contracts.form.paperwork_fee_payment',
            content: paperwork_fee_payment
              ? t(`contracts.tags.${paperwork_fee_payment}`)
              : '-'
          },
          {
            label: 'contracts.form.fraction_type',
            content: t(`contracts.form.options.${fraction_type}`)
          },
          {
            label: 'contracts.form.payment_type',
            content: t(`contracts.form.options.${payment_type}`)
          }
        ])
  ];

  const bankInfo = [
    ...(payment_type === 'DIRECTDEBIT'
      ? [
          {
            label: 'contracts.form.bank_name',
            content: bank_name,
            span: 3
          },
          {
            label: 'validate_quote.form.bank_address',
            content: bank_address
          },
          {
            label: 'validate_quote.form.bank_postal_code',
            content: bank_postal_code
          },
          {
            label: 'validate_quote.form.bank_city',
            content: bank_city
          },
          {
            label: 'contracts.form.bic',
            content: bic
          },
          {
            label: 'contracts.form.iban',
            content: iban
          },
          {
            label: 'validate_quote.form.rib_owner',
            content: t(
              `quotes.form.switch.${rib_not_subscriber ? 'no' : 'yes'}`
            )
          }
        ]
      : [
          ...(payment_type === 'TRANSFER'
            ? [
                {
                  label: `validate_quote.payment_type.transfer_iban`,
                  content: 'FR7630004005380001028052013'
                }
              ]
            : [])
        ])
  ];

  const contractStatusInfo = [
    {
      label: 'contracts.form.contract_status_info.insurer_name',
      content: insurer && insurer.name,
      span: 3
    },
    {
      label: 'contracts.form.contract_status_info.status',
      content: status ? t(`contracts.tags.${status}`) : 'Aucune donnée'
    },
    {
      label: 'contracts.form.contract_status_info.email_sending',
      content: t(`quotes.form.switch.${email_sending ? 'yes' : 'no'}`),
      span: 2
    },
    {
      label: 'contracts.form.contract_status_info.termination_reason',
      content: termination_reason
        ? t(`contracts.tags.${termination_reason}`)
        : 'Aucune donnée'
    },
    {
      label: 'contracts.form.contract_status_info.termination_date',
      content: termination_date && moment(termination_date).format('ll')
    },
    {
      label: 'contracts.form.contract_status_info.termination_asking_date',
      content:
        termination_asking_date && moment(termination_asking_date).format('ll')
    },
    {
      label: 'contracts.form.contract_status_info.termination_comment',
      content: termination_comment,
      span: 3
    }
  ];

  const costsInfo = {
    rcp: baseCostsInfo,
    rcpwk: baseCostsInfo,
    rcpcm: baseCostsInfo,
    rcgls: [
      ...baseCostsInfo.filter(
        (info) => info.label !== 'contracts.show.activities'
      ),
      {
        label: `contracts.form.main_activity_show`,
        content: `${MAIN_ACTIVITY_ENUM[main_activity]} - (${main_activity})`
      }
    ],
    rcpml: baseCostsInfo,
    mri: baseCostsInfo.filter(
      (d) =>
        ![
          'contracts.form.company_former',
          'contracts.form.selected_activities'
        ].includes(d.label)
    ),
    mriml: baseCostsInfo.filter(
      (d) =>
        ![
          'contracts.form.company_former',
          'contracts.form.selected_activities'
        ].includes(d.label)
    ),
    pnoml: baseCostsInfo.filter(
      (d) =>
        ![
          'contracts.form.company_former',
          'contracts.form.selected_activities'
        ].includes(d.label)
    ),
    mrp: baseCostsInfo,
    mrpwk: baseCostsInfo,
    mrpcm: baseCostsInfo,
    mrpml: baseCostsInfo,
    association: baseCostsInfo,
    prevoyance: baseCostsInfo,
    do: baseCostsInfo.filter(
      (info) => info.label !== 'contracts.show.activities'
    ),
    rcpmk: [
      ...baseCostsInfo.filter(
        (info) => info.label !== 'contracts.show.activities'
      ),
      {
        label: `contracts.form.activity_type`,
        content: activityTypeLabelMap[activity_type]
      },
      ...arrayConditionnalElement(
        {
          label: 'contracts.form.service_activity_details',
          content: (service_activity_details || []).join(', '),
          key: 'activity_detail'
        },
        activity_type === 'SERVICE'
      )
    ],
    rcngawk: [
      ...baseCostsInfo.filter(
        (info) => info.label !== 'contracts.show.activities'
      ),
      {
        label: `contracts.form.activity`,
        content: activity
      }
    ],
    rcphx: [
      ...baseCostsInfo,
      {
        label: `contracts.form.activity_type`,
        content: activityRCPHXTypeLabelMap[activity_type]
      },
      {
        label: 'contracts.show.rcphx.activity_detail',
        content: (activity_detail || []).join(', ')
      }
    ],
    mriwk: [
      ...baseCostsInfo.filter(
        (info) => info.label !== 'contracts.show.activities'
      ),
      {
        label: `contracts.show.mriwk.address`,
        key: 'address',
        content: contract?.address
          ? `${contract.address.number} ${contract.address.street}, ${contract.address.postal_code} ${contract.address.city}`
          : '',
        isEditable: true,
        isExemption: exemptions?.includes('address.city')
      }
    ]
  };

  const contractInfo = {
    rcp: [
      {
        label: 'contracts.form.guarantee_limit',
        content: `${numeral(guarantee_limit).format('0,0')}€`,
        key: 'guarantee_limit'
      },
      {
        label: 'contracts.form.deductible_amount',
        content: `${numeral(deductible_amount || 0).format('0,0')}€`,
        key: 'deductible_amount',
        span: 2
      },
      {
        label: 'contracts.form.employees_number',
        content: `${numeral(employees_number || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.turnover',
        content: turnover && `${numeral(turnover || 0).format('0,0')}`,
        span: 2
      },
      {
        label: 'contracts.form.company_in_creation',
        content: t(`quotes.form.switch.${company_in_creation ? 'yes' : 'no'}`)
      }
    ],
    rcpwk: [
      {
        label: 'contracts.form.guarantee_limit',
        content: `${numeral(guarantee_limit).format('0,0')}€`,
        key: 'guarantee_limit'
      },
      {
        label: 'contracts.form.deductible_amount',
        content: `${numeral(deductible_amount || 0).format('0,0')}€`,
        key: 'deductible_amount',
        span: 2
      },
      {
        label: 'contracts.form.employees_number',
        content: `${numeral(employees_number || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.turnover',
        content: turnover && `${numeral(turnover || 0).format('0,0')}`,
        span: 2
      },
      {
        label: 'contracts.form.company_in_creation',
        content: t(`quotes.form.switch.${company_in_creation ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.is_in_liquidation_or_receivership',
        content: t(
          `quotes.form.switch.${
            is_in_liquidation_or_receivership ? 'yes' : 'no'
          }`
        )
      }
    ],
    rcngawk: [
      {
        label: 'contracts.form.turnover',
        content: turnover && `${numeral(turnover || 0).format('0,0')}`,
        span: 2
      },
      {
        label: 'contracts.form.w_garage_certificate_number',
        content:
          w_garage_certificate_number &&
          `${numeral(w_garage_certificate_number || 0).format('0,0')}`,
        key: 'w_garage_certificate_number',
        span: 2
      },
      {
        label: 'contracts.form.w_garage_certificate',
        content:
          w_garage_certificate &&
          w_garage_certificate.map((element) => {
            return <p key={element}>{element}</p>;
          }),
        key: 'w_garage_certificate',
        span: 2
      },
      {
        label: 'contracts.form.guarantee_package',
        content: t(`contracts.form.options.${guarantee_package}`),
        key: 'guarantee_package',
        span: 2
      },
      {
        key: 'criminal_defense_recourse',
        label: 'contracts.show.rncgawk.criminal_defense_recourse',
        content: t('quotes.form.switch.yes'),
        span: 1
      },
      {
        key: 'individual_accident',
        label: 'contracts.show.rncgawk.individual_accident',
        content: t('quotes.form.switch.yes'),
        span: 1
      },
      {
        key: 'rc_pro_guarantee',
        label: 'contracts.show.rncgawk.rc_pro_guarantee',
        content: t('quotes.form.switch.yes'),
        span: 1
      },
      {
        key: 'rc_environmental_damage_guarantee',
        label: 'contracts.show.rncgawk.rc_environmental_damage_guarantee',
        content: t('quotes.form.switch.yes'),
        span: 1
      },
      {
        key: 'rc_exploitation_guarantee',
        label: 'contracts.show.rncgawk.rc_exploitation_guarantee',
        content: t('quotes.form.switch.yes'),
        span: 1
      },
      {
        key: 'rc_employer',
        label: 'contracts.show.rncgawk.rc_employer',
        content: t('quotes.form.switch.yes'),
        span: 1
      },
      {
        key: 'rc_trafic_guarantee',
        label: 'contracts.show.rncgawk.rc_trafic_guarantee',
        content: t('quotes.form.switch.yes'),
        span: 1
      },
      ...arrayConditionnalElement(
        [
          {
            key: 'theft_guarantee',
            label: 'contracts.show.rncgawk.theft_guarantee',
            content: t(
              `quotes.form.switch.${
                ['PKG_3', 'PKG_4'].includes(contract?.guarantee_package)
                  ? 'yes'
                  : 'no'
              }`
            ),
            span: 1
          },
          {
            key: 'fire_guarantee',
            label: 'contracts.show.rncgawk.fire_guarantee',
            content: t(
              `quotes.form.switch.${
                ['PKG_3', 'PKG_4'].includes(contract?.guarantee_package)
                  ? 'yes'
                  : 'no'
              }`
            ),
            span: 1
          }
        ],
        ['PKG_3', 'PKG_4'].includes(contract?.guarantee_package)
      ),
      ...arrayConditionnalElement(
        [
          {
            key: 'damage_all_accidents_guarantee',
            label: 'contracts.show.rncgawk.damage_all_accidents_guarantee',
            content: t(
              `quotes.form.switch.${
                contract?.guarantee_package === 'PKG_4' ? 'yes' : 'no'
              }`
            ),
            span: 1
          },
          {
            key: 'third_party_collision_guarantee',
            label: 'contracts.show.rncgawk.third_party_collision_guarantee',
            content: t(
              `quotes.form.switch.${
                contract?.guarantee_package === 'PKG_4' ? 'yes' : 'no'
              }`
            ),
            span: 1
          }
        ],
        contract?.guarantee_package === 'PKG_4'
      ),
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.form.glass_breakage_guarantee.label',
            content: contract?.glass_breakage_guarantee
              ? t('quotes.form.switch.true')
              : t('quotes.form.switch.false'),
            key: 'glass_breakage_guarantee',
            span: 1
          },
          {
            label: 'contracts.form.hail_guarantee.label',
            content: contract?.hail_guarantee
              ? t('quotes.form.switch.true')
              : t('quotes.form.switch.false'),
            key: 'hail_guarantee',
            span: 1
          }
        ],
        (contract?.guarantee_package === 'PKG_3' ||
          contract?.guarantee_package === 'PKG_4') &&
          contract?.turnover >= 1000000
      ),
      {
        label: 'contracts.form.max_fleet_size.label',
        content: contract?.max_fleet_size
          ? t(`contracts.form.max_fleet_size.${contract.max_fleet_size}`)
          : '-',
        key: 'max_fleet_size',
        span: 1
      },
      {
        label: 'contracts.form.vehicle_value_range.label',
        content: contract?.vehicle_value_range
          ? t(
              `contracts.form.vehicle_value_range.${contract.vehicle_value_range}`
            )
          : '-',
        key: 'vehicle_value_range',
        span: 2
      },
      {
        label: 'contracts.form.entrusted_vehicles.label',
        content: contract?.entrusted_vehicles
          ? t(
              `contracts.form.entrusted_vehicles.${contract.entrusted_vehicles}`
            )
          : '-',
        key: 'entrusted_vehicles',
        span: 1
      }
    ],
    rcphx: [
      {
        label: 'contracts.form.guarantee_limit',
        content: `${numeral(guarantee_limit).format('0,0')}€`,
        key: 'guarantee_limit'
      },
      {
        label: 'contracts.show.deductible_amount',
        content: `${numeral(deductible_amount || 0).format('0,0')}€`,
        key: 'deductible_amount',
      },
      {
        label: 'contracts.form.deductible_amount_rce_display',
        content: (
            `${numeral(
              getRCPHXDeductibleAmountRCE(
                activity_type,
                turnover,
                guarantee_limit
              )
            ).format('0,0')}€`
        ),
        key: 'deductible_amount_rce',
      },
      {
        label: 'contracts.form.turnover',
        content: turnover && `${numeral(turnover || 0).format('0,0')}`,
        span: 2
      },
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.form.turnover_usa_canada',
            content: t(
              `quotes.form.switch.${turnover_usa_canada ? 'yes' : 'no'}`
            )
          },
          {
            label: 'contracts.form.turnover_usa_canada_amount_display',
            content: `${numeral(turnover_usa_canada_amount).format('0,0')}${
              turnover_usa_canada_amount_type === 'PERCENTAGE' ? '%' : '€'
            }`
          }
        ],
        RCPHX_WORLDWIDE_ALLOWED_ACTIVITIES.includes(activity_type)
      )
    ],
    rcpcm: [
      {
        label: 'contracts.form.guarantee_limit',
        content: `${numeral(guarantee_limit).format('0,0')}€`,
        key: 'guarantee_limit'
      },
      {
        label: 'contracts.form.deductible_amount',
        content: `${numeral(deductible_amount || 0).format('0,0')}€`,
        key: 'deductible_amount',
        span: 2
      },
      {
        label: 'contracts.form.employees_number',
        content: `${numeral(employees_number || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.turnover',
        content: turnover && `${numeral(turnover || 0).format('0,0')}`,
        span: 2
      },
      {
        label: 'contracts.form.company_in_creation',
        content: t(`quotes.form.switch.${company_in_creation ? 'yes' : 'no'}`)
      }
    ],
    rcgls: [
      {
        label: 'contracts.form.guarantee_ceiling',
        content: `${numeral(guarantee_limit).format('0,0')}€`,
        key: 'guarantee_limit'
      },
      // {
      //   label: 'contracts.form.deductible_amount',
      //   content: `${numeral(deductible_amount || 0).format('0,0')}€`,
      //   key: 'deductible_amount',
      //   span: 2
      // },
      {
        label: 'contracts.form.turnover',
        content: turnover && `${numeral(turnover || 0).format('0,0')}`
      },
      // {
      //   label: 'contracts.form.company_in_creation',
      //   content: t(`quotes.form.switch.${company_in_creation ? 'yes' : 'no'}`)
      // }
      ...(
        {
          COA: [
            'insurance_brokers',
            'operational_liability',
            'iobsp_broker_errors',
            'iobsp_exclusive_agent_errors',
            'iobsp_non_exclusive_agent_errors',
            'iobsp_mandate_professional',
            'banking_solicitor_errors'
          ],
          MIA: ['operational_liability', 'insurance_sub_agents_errors'],
          IOBSP: [
            'operational_liability',
            'insurance_sub_agents_errors',
            'banking_and_finance_intermediary_professional',
            'borrower_insurance_intermediary_errors'
          ],
          MIOBSP: ['operational_liability', 'iobsp_mandate_professional'],
          CGP_CIF: [
            'operational_liability',
            'wealth_and_financial_advisor_errors'
          ],
          AI: ['operational_liability', 'real_estate_transaction_errors'],
          ACI: ['operational_liability', 'real_estate_transaction_errors']
        }[main_activity] || []
      ).map((key) => ({
        label: `contracts.form.${key}`,
        key,
        content: t(`quotes.form.switch.${contract?.[key] ? 'yes' : 'no'}`)
      }))
    ],
    rcpml: [
      {
        label: 'contracts.form.guarantee_limit',
        content: `${numeral(guarantee_limit).format('0,0')}€`,
        key: 'guarantee_limit'
      },
      {
        label: 'contracts.form.deductible_amount',
        content: `${numeral(deductible_amount || 0).format('0,0')}€`,
        key: 'deductible_amount',
        span: 2
      },
      {
        label: 'contracts.form.employees_number',
        content: `${numeral(employees_number || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.turnover',
        content: turnover && `${numeral(turnover || 0).format('0,0')}`,
        span: 2
      },
      {
        label: 'contracts.form.company_in_creation',
        content: t(`quotes.form.switch.${company_in_creation ? 'yes' : 'no'}`)
      }
    ],
    association: [
      {
        label: 'contracts.form.deductible_amount',
        content: `${numeral(deductible_amount || 0).format('0,0')}€`,
        key: 'deductible_amount',
        span: 2
      },
      {
        label: 'contracts.form.employees_number',
        trad: 'contracts.form.employees_number_asso',
        content: `${numeral(employees_number || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.representatives_number',
        content: `${numeral(representatives_number || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.total_members',
        content: `${numeral(total_members || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.yearly_events',
        content: yearly_events
          ? t(`contracts.form.association.yearly_events.${yearly_events}`)
          : '-'
      },
      ...(yearly_events !== '0'
        ? [
            {
              label: 'contracts.form.participants_per_event',
              content: participants_per_event
                ? t(
                    `contracts.form.association.participants_per_event.${participants_per_event}`
                  )
                : '-'
            }
          ]
        : []),
      {
        label: 'contracts.form.turnover',
        trad: 'contracts.form.budget',
        content: turnover && `${numeral(turnover || 0).format('0,0')}`,
        span: 2
      },
      ...(local
        ? [
            {
              label: 'contracts.form.sites',
              content: sites && sites?.length && (
                <div>
                  {sites.map((site) => (
                    <div>
                      <span>
                        {`• ${site.address?.number} ${site.address?.street} ${site.address?.city} 
                          `}
                      </span>
                    </div>
                  ))}
                </div>
              )
            },
            {
              label: 'contracts.form.resident_status',
              content: sites && sites?.length && (
                <div>
                  {sites.map((site) => (
                    <div>
                      <span>
                        {`
                    ${t(`contracts.form.options.${site.resident_status}`)}
                          `}
                      </span>
                    </div>
                  ))}
                </div>
              )
            },
            {
              label: 'contracts.form.surface_area_sites',
              content: sites?.length && (
                <div>
                  {sites.map((site) => (
                    <div>
                      <span>{`${site.surface_area} m²`}</span>
                    </div>
                  ))}
                </div>
              )
            },
            {
              label: 'contracts.form.fire_explosion_attack_climatic_event',
              content: `${numeral(
                fire_explosion_attack_climatic_event || 0
              ).format('0,0')}€`
            },
            {
              label: 'contracts.form.water_damage',
              content: `${numeral(water_damage || 0).format('0,0')}€`
            },
            {
              label: 'contracts.form.theft_vandalism',
              content: `${numeral(theft_vandalism || 0).format('0,0')}€`
            },
            {
              label: 'contracts.form.broken_windows',
              content: `${numeral(broken_windows || 0).format('0,0')}€`
            },
            {
              label: 'contracts.form.electric_damage',
              content: `${numeral(electric_damage || 0).format('0,0')}€`
            },
            {
              label: 'contracts.form.light_inflatable_structure',
              content: t(
                `quotes.form.switch.${
                  light_inflatable_structure ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.legal_protection',
              content: t(
                `quotes.form.switch.${legal_protection ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.extended_legal_protection',
              content: t(
                `quotes.form.switch.${extended_legal_protection ? 'yes' : 'no'}`
              )
            }
          ]
        : [
            {
              label: 'contracts.form.legal_protection',
              content: t(
                `quotes.form.switch.${legal_protection ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.civil_responsibility_asso',
              content: t(
                `quotes.form.switch.${civil_responsibility_asso ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.local_insurance',
              content: t(`quotes.form.switch.${local_insurance ? 'yes' : 'no'}`)
            }
          ])
    ],
    mri: [
      {
        label: 'contracts.form.subscriber_type',
        content:
          subscriber_type === 'OTHER'
            ? subscriber_type_other
            : t(`contracts.tags.${subscriber_type}`),
        span: 1
      },
      {
        label: 'contracts.form.buildings',
        content:
          buildings &&
          buildings[0]?.address?.number &&
          `${buildings[0]?.address?.number} ${buildings[0]?.address?.street}`,
        span: 1
      },
      {
        label: 'contracts.form.risk_address_pc',
        content:
          buildings &&
          buildings[0]?.address?.number &&
          `${buildings[0]?.address?.postal_code} ${buildings[0]?.address?.city}`,
        span: 1
      },
      {
        label: 'contracts.form.building_name',
        content: buildings && `${buildings[0]?.building_name}`
      },
      {
        label: 'contracts.form.building_siret',
        content: buildings && `${buildings[0]?.building_siret}`
      },
      {
        label: 'contracts.form.building_type',
        content:
          building_type === 'OTHER'
            ? building_type_other
            : t(`contracts.tags.${building_type}`)
      },
      {
        label: 'contracts.form.main_use',
        content: building_type
          ? t(`contracts.tags.${main_use}`)
          : 'Aucune donnée'
      },
      {
        label: 'contracts.form.building_category',
        content: building_type
          ? t(`contracts.tags.${building_category}`)
          : 'Aucune donnée'
      },
      {
        label: 'contracts.form.building_condition',
        content: building_type
          ? t(`contracts.tags.${building_condition}`)
          : 'Aucune donnée'
      },
      {
        label: 'contracts.form.construction_year',
        content:
          construction_year && `${moment(construction_year).format('YYYY')}`
      },
      {
        label: 'contracts.form.surface_area',
        content: surface_area && `${surface_area} m2`
      },
      {
        label: 'contracts.form.total_buildings',
        content: total_buildings && `${total_buildings}`
      },
      {
        label: 'contracts.form.risk_typology',
        content:
          risk_typology === 'OTHER'
            ? risk_typology_other
            : t(`contracts.tags.${risk_typology}`)
      },
      ...(risk_typology === 'FURNISHED'
        ? [
            {
              label: 'contracts.form.furniture_capital',
              content: furniture_capital && furniture_capital
            },
            ...(furniture_capital === 'OTHER'
              ? [
                  {
                    label: 'contracts.form.furniture_capital_other',
                    content: furniture_capital_other && furniture_capital_other
                  }
                ]
              : [])
          ]
        : []),
      {
        label: 'contracts.form.concrete_floor',
        content: t(`quotes.form.switch.${concrete_floor ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.empty_unoccupied_works',
        content: t(
          `quotes.form.switch.${empty_unoccupied_works ? 'yes' : 'no'}`
        ),
        span: 2
      },
      {
        label: 'contracts.form.guarantee_type',
        content: (
          <div>
            {guarantee_type
              ? t(`contracts.tags.${guarantee_type}`)
              : 'Aucune donnée'}
            <Popover
              placement="right"
              content={t('contracts.form.guarantee_type_open')}
            >
              <EyeOutlined
                style={{ marginLeft: 5 }}
                onClick={() => setOpenFormuleModal(true)}
              />
            </Popover>
            <GuaranteesModal
              setOpenFormuleModal={setOpenFormuleModal}
              openFormuleModal={openFormuleModal}
              contract={contract}
            />
          </div>
        )
      },
      ...(empty_unoccupied_works
        ? [
            {
              label: 'contracts.form.empty_reason',
              content: empty_reason
                ? t(`contracts.tags.${empty_reason}`)
                : 'Aucune donnée'
            }
          ]
        : []),
      {
        label: 'contracts.form.deductible_cost_index',
        content: deductible_cost_index
          ? t(`contracts.tags.${deductible_cost_index}`)
          : 'Aucune donnée'
      },
      {
        label: 'contracts.form.g1231_property_owner_civil_liability',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_property_owner_civil_liability ? 'yes' : 'no'
          }`
        )
      },
      {
        label:
          'contracts.form.g1231_criminal_defense_recourse_following_accident',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_criminal_defense_recourse_following_accident
              ? 'yes'
              : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_juridic_protection',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_juridic_protection ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_natural_disasters',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_natural_disasters ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_technological_risks',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_technological_risks ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_fire_related_risks',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_fire_related_risks ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_attacks',
        content: t(
          `quotes.form.switch.${contract?.g1231_attacks ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g1231_gareat',
        content: t(
          `quotes.form.switch.${contract?.g1231_gareat ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g1231_union_civil_liability',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_union_civil_liability ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g0230_storms_hail_heavy_snow',
        content: t(
          `quotes.form.switch.${
            contract?.g0230_storms_hail_heavy_snow ||
            contract?.g1231_storms_hail_heavy_snow
              ? 'yes'
              : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g0230_collapse',
        content: t(
          `quotes.form.switch.${contract?.g0230_collapse ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0230_theft',
        content: t(`quotes.form.switch.${contract?.g0230_theft ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.g0230_electrical_damage',
        content: t(
          `quotes.form.switch.${
            contract?.g0230_electrical_damage ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g0030_broken_glass',
        content: t(
          `quotes.form.switch.${contract?.g0030_broken_glass ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0030_water_damage',
        content: t(
          `quotes.form.switch.${contract?.g0030_water_damage ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0030_vandalism',
        content: t(
          `quotes.form.switch.${contract?.g0030_vandalism ? 'yes' : 'no'}`
        )
      }
    ],
    mriml: [
      {
        label: 'contracts.form.subscriber_type',
        content:
          subscriber_type === 'OTHER'
            ? subscriber_type_other
            : t(`contracts.tags.${subscriber_type}`),
        span: 1
      },
      {
        label: 'contracts.form.buildings',
        content:
          buildings &&
          buildings[0]?.address?.number &&
          `${buildings[0]?.address?.number} ${buildings[0]?.address?.street}`,
        span: 1
      },
      {
        label: 'contracts.form.risk_address_pc',
        content:
          buildings &&
          buildings[0]?.address?.number &&
          `${buildings[0]?.address?.postal_code} ${buildings[0]?.address?.city}`,
        span: 1
      },
      {
        label: 'contracts.form.building_name',
        content: buildings && `${buildings[0]?.building_name}`
      },
      {
        label: 'contracts.form.building_siret',
        content: buildings && `${buildings[0]?.building_siret}`
      },
      {
        label: 'contracts.form.main_use',
        content: main_use ? t(`contracts.tags.${main_use}`) : 'Aucune donnée'
      },
      {
        label: 'contracts.form.specific_risk_show',
        key: 'specific_risk',
        content: specific_risk
          ? t(`contracts.tags.${specific_risk}`)
          : 'Aucune donnée'
      },
      {
        label: 'contracts.form.construction_year',
        content:
          construction_year && `${moment(construction_year).format('YYYY')}`
      },
      {
        label: 'contracts.form.acquisition_date',
        content:
          acquisition_date && `${moment(acquisition_date).format('DD-MM-YYYY')}`
      },
      ...(acquisition_date &&
      moment(contract.created_at).diff(acquisition_date, 'days') > 60
        ? [
            {
              label: 'contracts.form.is_continuously_insured_since_acquisition',
              content: t(
                `quotes.form.switch.${
                  is_continuously_insured_since_acquisition ? 'yes' : 'no'
                }`
              )
            }
          ]
        : []),
      {
        label: 'contracts.form.surface_area',
        content: surface_area && `${surface_area} m2`
      },
      {
        label: 'contracts.form.total_buildings',
        content: total_buildings && `${total_buildings}`
      },
      {
        label: 'contracts.form.risk_typology',
        content:
          risk_typology === 'OTHER'
            ? risk_typology_other
            : t(`contracts.tags.${risk_typology}`)
      },
      {
        label: 'contracts.form.absence_concrete_floor',
        content: t(
          `quotes.form.switch.${absence_concrete_floor ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.empty_unoccupied_works',
        content: t(
          `quotes.form.switch.${empty_unoccupied_works ? 'yes' : 'no'}`
        ),
        span: 2
      },
      {
        label: 'contracts.form.guarantee_type',
        content: (
          <div>
            {guarantee_type
              ? t(`contracts.tags.${guarantee_type}`)
              : 'Aucune donnée'}
            {/* <Popover
              placement="right"
              content={t('contracts.form.guarantee_type_open')}
            >
              <EyeOutlined
                style={{ marginLeft: 5 }}
                onClick={() => setOpenFormuleModal(true)}
              />
            </Popover>
            <GuaranteesModal
              setOpenFormuleModal={setOpenFormuleModal}
              openFormuleModal={openFormuleModal}
              contract={contract}
            /> */}
          </div>
        )
      },
      ...(empty_unoccupied_works
        ? [
            {
              label: 'contracts.form.empty_reason',
              content: empty_reason
                ? t(`contracts.tags.${empty_reason}`)
                : 'Aucune donnée'
            }
          ]
        : []),
      {
        label: 'contracts.form.deductible_cost_index',
        content: deductible_cost_index
          ? t(`contracts.tags.${deductible_cost_index}`)
          : 'Aucune donnée'
      },
      {
        label: 'contracts.form.g1231_property_owner_civil_liability',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_property_owner_civil_liability ? 'yes' : 'no'
          }`
        )
      },
      {
        label:
          'contracts.form.g1231_criminal_defense_recourse_following_accident',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_criminal_defense_recourse_following_accident
              ? 'yes'
              : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_natural_disasters',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_natural_disasters ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g0230_climatic_events',
        content: t(
          `quotes.form.switch.${contract?.g0230_climatic_events ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0230_motorized_vehicle_impact',
        content: t(
          `quotes.form.switch.${
            contract?.g0230_motorized_vehicle_impact ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_technological_risks',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_technological_risks ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_fire_related_risks',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_fire_related_risks ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_attacks',
        content: t(
          `quotes.form.switch.${contract?.g1231_attacks ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0230_theft_vandalism',
        content: t(
          `quotes.form.switch.${contract?.g0230_theft_vandalism ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0230_electrical_damage',
        content: t(
          `quotes.form.switch.${
            contract?.g0230_electrical_damage ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g0030_broken_glass',
        content: t(
          `quotes.form.switch.${contract?.g0030_broken_glass ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0030_broken_machine',
        content: t(
          `quotes.form.switch.${contract?.g0030_broken_machine ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0030_water_damage',
        content: t(
          `quotes.form.switch.${contract?.g0030_water_damage ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0230_collapse',
        content: t(
          `quotes.form.switch.${contract?.g0230_collapse ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.rc_volunteer_union',
        content: t(`quotes.form.switch.${rc_volunteer_union ? 'yes' : 'no'}`)
      }
    ],
    pnoml: [
      {
        label: 'contracts.form.subscriber_type',
        content:
          subscriber_type === 'OTHER'
            ? subscriber_type_other
            : t(`contracts.tags.${subscriber_type}`),
        span: 1
      },
      {
        label: 'contracts.form.buildings',
        content:
          buildings &&
          buildings[0]?.address?.number &&
          `${buildings[0]?.address?.number} ${buildings[0]?.address?.street}`,
        span: 1
      },
      {
        label: 'contracts.form.risk_address_pc',
        content:
          buildings &&
          buildings[0]?.address?.number &&
          `${buildings[0]?.address?.postal_code} ${buildings[0]?.address?.city}`,
        span: 1
      },
      {
        label: 'contracts.form.building_name',
        content: buildings && `${buildings[0]?.building_name}`
      },
      {
        label: 'contracts.form.building_siret',
        content: buildings && `${buildings[0]?.building_siret}`
      },
      {
        label: 'contracts.form.main_use',
        content: t(`contracts.tags.${main_use}`)
      },
      {
        label: 'contracts.form.surface_area',
        content: surface_area && `${surface_area} m2`
      },
      {
        label: 'contracts.form.total_buildings',
        content: total_buildings && `${total_buildings}`
      },
      {
        label: 'contracts.form.property_under_construction',
        content: t(
          `quotes.form.switch.${property_under_construction ? 'yes' : 'no'}`
        ),
        span: 2
      },
      {
        label: 'contracts.form.guarantee_type',
        content: (
          <div>
            {guarantee_type
              ? t(`contracts.tags.${guarantee_type}`)
              : 'Aucune donnée'}
          </div>
        )
      },
      ...(property_under_construction
        ? [
            {
              label: 'contracts.form.under_construction_duration',
              content: under_construction_duration
                ? t(`contracts.tags.${under_construction_duration}`)
                : 'Aucune donnée'
            }
          ]
        : []),
      {
        label: 'contracts.form.deductible_cost_index',
        content: deductible_cost_index
          ? t(`contracts.tags.${deductible_cost_index}`)
          : 'Aucune donnée'
      },
      {
        label: 'contracts.form.g1231_fire_related_risks',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_fire_related_risks ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g0230_motorized_vehicle_impact',
        content: t(
          `quotes.form.switch.${
            contract?.g0230_motorized_vehicle_impact ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_natural_disasters',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_natural_disasters ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_technological_risks',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_technological_risks ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.g1231_attacks',
        content: t(
          `quotes.form.switch.${contract?.g1231_attacks ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0230_climatic_events',
        content: t(
          `quotes.form.switch.${contract?.g0230_climatic_events ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0230_theft_vandalism',
        content: t(
          `quotes.form.switch.${contract?.g0230_theft_vandalism ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0030_water_damage',
        content: t(
          `quotes.form.switch.${contract?.g0030_water_damage ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g0030_broken_glass',
        content: t(
          `quotes.form.switch.${contract?.g0030_broken_glass ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g1231_civil_liability',
        content: t(
          `quotes.form.switch.${contract?.g1231_civil_liability ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.g1231_criminal_defense_recourse',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_criminal_defense_recourse ? 'yes' : 'no'
          }`
        )
      }
    ],
    mrp: [
      {
        label: 'contracts.form.deductible_amount',
        content: `${numeral(deductible_amount || 0).format('0,0')}€`,
        key: 'deductible_amount',
        span: 2
      },
      {
        label: 'contracts.form.sites',
        content: sites?.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>
                  {`• ${site.address?.number} ${site.address?.street} ${site.address?.city}`}
                </span>
              </div>
            ))}
          </div>
        ),
        isExemption:
          exemptions?.includes('postcode') || exemptions?.includes('sites')
      },
      {
        label: 'contracts.form.resident_status',
        content: sites && sites?.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>
                  {`
                  ${t(`contracts.form.options.${site.resident_status}`)}
                        `}
                </span>
              </div>
            ))}
          </div>
        )
      },
      {
        label: 'contracts.form.surface_area_sites',
        content: sites && sites?.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>{`${site.surface_area} m²`}</span>
              </div>
            ))}
          </div>
        )
      },
      {
        label: 'contracts.form.employees_number',
        content: `${numeral(employees_number || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.fire_explosion_attack_climatic_event',
        content: `${numeral(fire_explosion_attack_climatic_event || 0).format(
          '0,0'
        )}€`
      },
      {
        label: 'contracts.form.attack',
        content: t(`quotes.form.switch.${attack ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.catastrophe',
        content: t(`quotes.form.switch.${catastrophe ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.tech_catastrophe',
        content: t(`quotes.form.switch.${tech_catastrophe ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.theft_vandalism',
        content: `${numeral(theft_vandalism || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.water_damage',
        content: `${numeral(water_damage || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.broken_windows',
        content: `${numeral(broken_windows || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.broken_machine',
        content: `${numeral(broken_machine || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.electric_damage',
        content: `${numeral(electric_damage || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.external_furniture',
        content: `${numeral(external_furniture || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.chilled_merchandise',
        content: `${numeral(chilled_merchandise || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.archives_capital',
        content: t(`quotes.form.switch.${archives_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.value_capital',
        content: t(`quotes.form.switch.${value_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.art_capital',
        content: t(`quotes.form.switch.${art_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.theft_capital',
        content: t(`quotes.form.switch.${theft_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.owner_insurance',
        content: t(`quotes.form.switch.${owner_insurance ? 'yes' : 'no'}`)
      },
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.exploitation_loss',
              content: t(
                `quotes.form.switch.${exploitation_loss ? 'yes' : 'no'}`
              )
            }
          ]),
      {
        label: 'contracts.form.tenant_responsibility',
        content: t(`quotes.form.switch.${tenant_responsibility ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.neighbours_appeal',
        content: t(`quotes.form.switch.${neighbours_appeal ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.tenant_appeal',
        content: t(`quotes.form.switch.${tenant_appeal ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.lawsuit_defense',
        content: t(`quotes.form.switch.${lawsuit_defense ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.lent_goods',
        content: t(`quotes.form.switch.${lent_goods ? 'yes' : 'no'}`)
      },
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.rc_pro',
              content: t(`quotes.form.switch.${rc_pro ? 'yes' : 'no'}`)
            },
            {
              label: 'contracts.form.social_mandate_responsibility',
              content: t(
                `quotes.form.switch.${
                  social_mandate_responsibility ? 'yes' : 'no'
                }`
              )
            }
          ]),
      {
        label: 'contracts.form.exploitation_responsibility',
        content: t(
          `quotes.form.switch.${exploitation_responsibility ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.legal_protection_mrp',
        content: t(`quotes.form.switch.yes`)
      },
      {
        label: 'contracts.form.extended_legal_protection_mrp',
        content: t(
          `quotes.form.switch.${extended_legal_protection_mrp ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.assistance',
        content: t(`quotes.form.switch.${assistance ? 'yes' : 'no'}`)
      }
    ],
    mrpwk: [
      {
        label: 'contracts.form.deductible_amount',
        content: `${numeral(deductible_amount || 0).format('0,0')}€`,
        key: 'deductible_amount',
        span: 2
      },
      {
        label: 'contracts.form.sites',
        content: sites && sites.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>
                  {`• ${site.address?.number} ${site.address?.street} ${
                    site.address?.city
                  } ${
                    !site.address?.city.includes('(')
                      ? `(${site.address?.postal_code})`
                      : ''
                  }
                      `}
                </span>
              </div>
            ))}
          </div>
        ),
        isExemption:
          exemptions?.includes('postcode') || exemptions?.includes('sites')
      },
      {
        label: 'contracts.show.mriwk.is_qpv',
        content: t(`quotes.form.switch.${is_qpv ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.resident_status',
        content: sites && sites?.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>
                  {`
                  ${t(`contracts.form.options.${site.resident_status}`)}
                        `}
                </span>
              </div>
            ))}
          </div>
        )
      },
      {
        label: 'contracts.form.surface_area_sites',
        content: sites && sites?.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>{`${site.surface_area} m²`}</span>
              </div>
            ))}
          </div>
        )
      },
      {
        label: 'contracts.form.employees_number',
        content: `${numeral(employees_number || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.fire_explosion_attack_climatic_event',
        content: `${numeral(fire_explosion_attack_climatic_event || 0).format(
          '0,0'
        )}€`
      },
      {
        label: 'contracts.form.attack',
        content: t(`quotes.form.switch.${attack ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.catastrophe',
        content: t(`quotes.form.switch.${catastrophe ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.tech_catastrophe',
        content: t(`quotes.form.switch.${tech_catastrophe ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.theft_vandalism',
        content: `${numeral(theft_vandalism || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.water_damage',
        content: `${numeral(water_damage || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.broken_windows',
        content: `${numeral(broken_windows || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.broken_machine',
        content: `${numeral(broken_machine || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.electric_damage',
        content: `${numeral(electric_damage || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.external_furniture',
        content: `${numeral(external_furniture || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.chilled_merchandise',
        content: `${numeral(chilled_merchandise || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.archives_capital',
        content: t(`quotes.form.switch.${archives_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.value_capital',
        content: t(`quotes.form.switch.${value_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.art_capital',
        content: t(`quotes.form.switch.${art_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.theft_capital',
        content: t(`quotes.form.switch.${theft_capital ? 'yes' : 'no'}`)
      },
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.exploitation_loss',
              content: t(
                `quotes.form.switch.${exploitation_loss ? 'yes' : 'no'}`
              )
            }
          ]),
      {
        label: 'contracts.form.tenant_responsibility',
        content: t(`quotes.form.switch.${tenant_responsibility ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.neighbours_appeal',
        content: t(`quotes.form.switch.${neighbours_appeal ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.tenant_appeal',
        content: t(`quotes.form.switch.${tenant_appeal ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.lawsuit_defense',
        content: t(`quotes.form.switch.${lawsuit_defense ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.lent_goods',
        content: t(`quotes.form.switch.${lent_goods ? 'yes' : 'no'}`)
      },
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.rc_pro',
              content: t(`quotes.form.switch.${rc_pro ? 'yes' : 'no'}`)
            },
            {
              label: 'contracts.form.rc_pro_guarantee',
              content: `${numeral(rc_pro_guarantee || 0).format('0,0')}€`
            }
          ]),
      {
        label: 'contracts.form.exploitation_responsibility',
        content: t(
          `quotes.form.switch.${exploitation_responsibility ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.legal_protection_mrp',
        content: t(`quotes.form.switch.yes`)
      },
      {
        label: 'contracts.form.extended_legal_protection_mrp',
        content: t(
          `quotes.form.switch.${extended_legal_protection_mrp ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.is_in_liquidation_or_receivership',
        content: t(
          `quotes.form.switch.${
            is_in_liquidation_or_receivership || liquidation ? 'yes' : 'no'
          }`
        )
      }
    ],
    mrpcm: [
      {
        label: 'contracts.form.deductible_amount',
        content: `${numeral(deductible_amount || 0).format('0,0')}€`,
        key: 'deductible_amount',
        span: 2
      },
      {
        label: 'contracts.form.sites',
        content: sites && sites.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>
                  {`• ${site.address?.number} ${site.address?.street} ${
                    site.address?.city
                  } ${
                    !site.address?.city.includes('(')
                      ? `(${site.address?.postal_code})`
                      : ''
                  }
                      `}
                </span>
              </div>
            ))}
          </div>
        ),
        isExemption:
          exemptions?.includes('postcode') || exemptions?.includes('sites')
      },
      {
        label: 'contracts.form.resident_status',
        content: sites && sites?.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>
                  {`
                  ${t(`contracts.form.options.${site.resident_status}`)}
                        `}
                </span>
              </div>
            ))}
          </div>
        )
      },
      {
        label: 'contracts.form.surface_area_sites',
        content: sites && sites?.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>{`${site.surface_area} m²`}</span>
              </div>
            ))}
          </div>
        )
      },
      {
        label: 'contracts.form.employees_number',
        content: `${numeral(employees_number || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.fire_explosion_attack_climatic_event',
        content: `${numeral(fire_explosion_attack_climatic_event || 0).format(
          '0,0'
        )}€`
      },
      {
        label: 'contracts.form.attack',
        content: t(`quotes.form.switch.${attack ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.catastrophe',
        content: t(`quotes.form.switch.${catastrophe ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.tech_catastrophe',
        content: t(`quotes.form.switch.${tech_catastrophe ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.theft_vandalism',
        content: `${numeral(theft_vandalism || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.water_damage',
        content: `${numeral(water_damage || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.broken_windows',
        content: `${numeral(broken_windows || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.broken_machine',
        content: `${numeral(broken_machine || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.electric_damage',
        content: `${numeral(electric_damage || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.external_furniture',
        content: `${numeral(external_furniture || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.chilled_merchandise',
        content: `${numeral(chilled_merchandise || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.archives_capital',
        content: t(`quotes.form.switch.${archives_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.value_capital',
        content: t(`quotes.form.switch.${value_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.art_capital',
        content: t(`quotes.form.switch.${art_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.theft_capital',
        content: t(`quotes.form.switch.${theft_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.owner_insurance',
        content: t(`quotes.form.switch.${owner_insurance ? 'yes' : 'no'}`)
      },
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.exploitation_loss',
              content: t(
                `quotes.form.switch.${exploitation_loss ? 'yes' : 'no'}`
              )
            }
          ]),
      {
        label: 'contracts.form.tenant_responsibility',
        content: t(`quotes.form.switch.${tenant_responsibility ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.neighbours_appeal',
        content: t(`quotes.form.switch.${neighbours_appeal ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.tenant_appeal',
        content: t(`quotes.form.switch.${tenant_appeal ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.lawsuit_defense',
        content: t(`quotes.form.switch.${lawsuit_defense ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.lent_goods',
        content: t(`quotes.form.switch.${lent_goods ? 'yes' : 'no'}`)
      },
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.rc_pro',
              content: t(`quotes.form.switch.${rc_pro ? 'yes' : 'no'}`)
            },
            {
              label: 'contracts.form.social_mandate_responsibility',
              content: t(
                `quotes.form.switch.${
                  social_mandate_responsibility ? 'yes' : 'no'
                }`
              )
            }
          ]),
      {
        label: 'contracts.form.exploitation_responsibility',
        content: t(
          `quotes.form.switch.${exploitation_responsibility ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.legal_protection_mrp',
        content: t(`quotes.form.switch.yes`)
      },
      {
        label: 'contracts.form.extended_legal_protection_mrp',
        content: t(
          `quotes.form.switch.${extended_legal_protection_mrp ? 'yes' : 'no'}`
        )
      }
    ],
    mrpml: [
      {
        label: 'contracts.form.deductible_amount',
        content: `${numeral(deductible_amount || 0).format('0,0')}€`,
        key: 'deductible_amount',
        span: 2
      },
      {
        label: 'contracts.form.turnover',
        content: `${numeral(turnover || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.sites',
        content: sites && sites.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>
                  {`• ${site.address?.number} ${site.address?.street} ${
                    site.address?.city
                  } ${
                    !site.address?.city.includes('(')
                      ? `(${site.address?.postal_code})`
                      : ''
                  }
                      `}
                </span>
              </div>
            ))}
          </div>
        ),
        isExemption:
          exemptions?.includes('postcode') || exemptions?.includes('sites')
      },
      {
        label: 'contracts.form.resident_status',
        content: sites && sites?.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>
                  {`
                  ${t(`contracts.form.options.${site.resident_status}`)}
                        `}
                </span>
              </div>
            ))}
          </div>
        )
      },
      {
        label: 'contracts.form.surface_area_sites',
        content: sites && sites?.length && (
          <div>
            {sites.map((site) => (
              <div>
                <span>{`${site.surface_area} m²`}</span>
              </div>
            ))}
          </div>
        )
      },
      {
        label: 'contracts.form.employees_number',
        content: `${numeral(employees_number || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.fire_explosion_attack_climatic_event',
        content: `${numeral(fire_explosion_attack_climatic_event || 0).format(
          '0,0'
        )}€`
      },
      {
        label: 'contracts.form.attack',
        content: t(`quotes.form.switch.${attack ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.catastrophe',
        content: t(`quotes.form.switch.${catastrophe ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.tech_catastrophe',
        content: t(`quotes.form.switch.${tech_catastrophe ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.theft_vandalism',
        content: `${numeral(theft_vandalism || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.water_damage',
        content: `${numeral(water_damage || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.broken_windows',
        content: `${numeral(broken_windows || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.broken_machine',
        content: `${numeral(broken_machine || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.electric_damage',
        content: `${numeral(electric_damage || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.external_furniture',
        content: `${numeral(external_furniture || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.chilled_merchandise',
        content: `${numeral(chilled_merchandise || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.archives_capital',
        content: t(`quotes.form.switch.${archives_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.value_capital',
        content: t(`quotes.form.switch.${value_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.theft_capital',
        content: t(`quotes.form.switch.${theft_capital ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.object_of_value',
        content: t(`quotes.form.switch.${object_of_value ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.additional_costs',
        content: t(`quotes.form.switch.${additional_costs ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.VTAM_choc_provisioire',
        content: t(`quotes.form.switch.${VTAM_choc_provisioire ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.operation_of_premises',
        content: t(`quotes.form.switch.${operation_of_premises ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.intoxication_alimentaire',
        content: t(
          `quotes.form.switch.${intoxication_alimentaire ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.lawsuit_defense',
        content: t(`quotes.form.switch.${lawsuit_defense ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.lent_goods',
        content: t(`quotes.form.switch.${lent_goods ? 'yes' : 'no'}`)
      },
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.rc_pro_guarantee',
              content: `${numeral(rc_pro_guarantee || 0).format('0,0')}€`
            }
          ]),
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.mobility_guarantee',
              content: `${numeral(mobility_guarantee || 0).format('0,0')}€`
            }
          ]),
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.business_loss_guarantee',
              content: t(
                `contracts.form.options.business_loss_${business_loss_guarantee}_months`
              )
            }
          ]),
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.market_value',
              content: `${numeral(market_value || 0).format('0,0')}€`
            }
          ]),
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.social_mandate_responsibility',
              content: t(
                `quotes.form.switch.${
                  social_mandate_responsibility ? 'yes' : 'no'
                }`
              )
            }
          ]),
      ...(activities && activities[0] === 'Activité de bureau'
        ? []
        : [
            {
              label: 'contracts.form.exploitation_responsibility',
              content: t(
                `quotes.form.switch.${
                  exploitation_responsibility ? 'yes' : 'no'
                }`
              )
            }
          ]),
      {
        label: 'contracts.form.key_man_guarantee',
        content: t(`quotes.form.switch.${key_man_guarantee ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.legal_protection_option',
        content: t(`quotes.form.switch.${legal_protection_mrp ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.extended_legal_protection_mrp',
        content: t(
          `quotes.form.switch.${extended_legal_protection_mrp ? 'yes' : 'no'}`
        )
      }
    ],
    prevoyance: [
      {
        label: 'contracts.form.package',
        content: t(`contracts.form.options.${pckg}`)
      },
      {
        label: 'contracts.form.capital',
        content: `${numeral(capital || 0).format('0,0')}€`,
        span: 2
      },
      {
        label: 'contracts.form.death',
        content: t(`quotes.form.switch.${death ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.disability',
        content: t(`quotes.form.switch.${disability ? 'yes' : 'no'}`)
      }
    ],
    do: [
      {
        label: 'contracts.form.turnover',
        content: turnover && `${numeral(turnover || 0).format('0,0')}`,
        span: 2
      },
      {
        label: 'contracts.form.guarantee_limit',
        content: `${numeral(guarantee_limit).format('0,0')}€`,
        key: 'guarantee_limit'
      },
      {
        label: 'contracts.form.company_in_creation',
        content: t(`quotes.form.switch.${company_in_creation ? 'yes' : 'no'}`),
        key: 'company_in_creation'
      }
    ],
    rcpmk: [
      {
        label: 'contracts.form.turnover',
        content: turnover && `${numeral(turnover || 0).format('0,0')}`
      },
      {
        label: 'contracts.form.guarantee_limit',
        content: `${numeral(guarantee_limit).format('0,0')}€`,
        key: 'guarantee_limit'
      },
      {
        label: 'contracts.form.deductible_amount',
        content: `${numeral(deductible_amount || 0).format('0,0')}€`,
        key: 'deductible_amount'
      },
      {
        label: 'contracts.form.is_startup',
        content: t(`quotes.form.switch.${is_startup ? 'yes' : 'no'}`)
      }
    ],
    mriwk: [
      {
        label: 'contracts.show.mriwk.lci',
        content: `${numeral(contractual_indemnity_limit || 0).format('0,0')}€`
      },
      {
        label: 'contracts.show.mriwk.is_qpv',
        content: t(`quotes.form.switch.${is_qpv ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.show.mriwk.building_count',
        content: contract?.building_count || 1
      },
      {
        label: 'contracts.show.mriwk.deductible_amount_ratio',
        content: t(`contracts.show.mriwk.options.deductible_amount_ratio`, {
          ratio: contract?.deductible_amount_ratio
        })
      },
      {
        label: 'contracts.show.mriwk.building_type',
        content: t(
          `contracts.show.mriwk.options.building_type.${building_type}`,
          {
            other: contract?.building_type_other
          }
        )
      },
      {
        label: 'contracts.show.mriwk.building_quality',
        content: t(
          `contracts.show.mriwk.options.building_quality.${contract?.building_quality}`
        )
      },
      {
        label: 'contracts.show.mriwk.surface_area',
        content: `${numeral(surface_area).format('0,0')}m²`
      },
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.show.mriwk.office_activity_percentage',
            content: `${contract?.office_activity_percentage || 0}%`
          },
          {
            label: 'contracts.show.mriwk.shop_activity_percentage',
            content: `${contract?.shop_activity_percentage || 0}%`
          }
        ],
        isWKMri({ building_type }) && contract?.contains_professional_activity
      ),
      {
        label: 'contracts.show.mriwk.is_vacant',
        content: t(`quotes.form.switch.${contract?.is_vacant ? 'yes' : 'no'}`)
      },
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.show.mriwk.vacant_reason',
            content: t(
              `contracts.show.mriwk.options.vacant_reason.${contract?.vacant_reason}`
            ),
            exemptionKey: 'is_vacant'
          },
          ...arrayConditionnalElement(
            [
              {
                label: 'contracts.show.mriwk.deadline_before_rental',
                content:
                  contract?.deadline_before_rental === 'LESS_THAN_3_MONTHS' ||
                  contract?.deadline_before_rental === 'MORE_THAN_3_MONTHS'
                    ? t(
                        `contracts.form.deadline_before_rental.${contract.deadline_before_rental}`
                      )
                    : 'N/A',
                exemptionKey: 'deadline_before_rental'
              }
            ],
            contract?.vacant_reason === 'EMPTY'
          ),
          {
            label: 'contracts.show.mriwk.vacant_area',
            content: t(
              `contracts.show.mriwk.options.vacant_area.${contract?.vacant_area}`
            ),
            exemptionKey: 'is_vacant'
          }
        ],
        contract?.is_vacant
      ),
      {
        label: 'contracts.show.mriwk.is_furnished',
        content: t(
          `contracts.show.mriwk.options.is_furnished.${contract?.is_furnished}`
        )
      },
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.show.mriwk.furniture_guarantee',
            content: `${numeral(contract?.furniture_guarantee).format('0,0')}€`
          }
        ],
        contract?.is_furnished === 'YES'
      ),
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.show.mriwk.other_furnished',
            content: contract?.other_furnished
          }
        ],
        contract?.is_furnished === 'OTHER'
      ),
      ...arrayConditionnalElement(
        {
          label: 'contracts.show.mriwk.is_condominium',
          content: t(
            `quotes.form.switch.${contract?.is_condominium ? 'yes' : 'no'}`
          )
        },
        isWKPno({ building_type })
      ),
      ...arrayConditionnalElement(
        {
          label: 'contracts.show.mriwk.outbuilding_surface',
          content: t(
            `${numeral(contract?.outbuilding_surface).format('0,0')}m²`
          )
        },
        isWKPno({ building_type }) && contract?.contains_outbuilding
      )
    ]
  };

  const optionInfo = {
    rcp: [
      {
        label: 'contracts.form.legal_protection',
        content: t(`quotes.form.switch.${legal_protection ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.extended_legal_protection',
        content: t(
          `quotes.form.switch.${extended_legal_protection ? 'yes' : 'no'}`
        ),
        span: 2
      },
      {
        label: 'contracts.form.exploitation_responsibility',
        content: t(
          `quotes.form.switch.${exploitation_responsibility ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.surface_area',
        key: 'surface_area',
        content: surface_area && surface_area
      },
      {
        label: 'contracts.form.social_mandate_responsibility',
        content: t(
          `quotes.form.switch.${social_mandate_responsibility ? 'yes' : 'no'}`
        )
      }
    ],
    rcpwk: [
      {
        label: 'contracts.form.legal_protection',
        content: t(`quotes.form.switch.${legal_protection ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.extended_legal_protection',
        content: t(
          `quotes.form.switch.${extended_legal_protection ? 'yes' : 'no'}`
        ),
        span: 2
      },
      {
        label: 'contracts.form.exploitation_responsibility',
        content: t(
          `quotes.form.switch.${exploitation_responsibility ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.surface_area',
        key: 'surface_area',
        content: surface_area && surface_area
      }
    ],
    rcphx: [
      {
        label: ['MED', 'OSTEO', 'BIE'].includes(activity_type)
          ? 'contracts.form.legal_protection_rcphx_optional'
          : 'contracts.form.legal_protection_rcphx',
        content: t(
          `quotes.form.switch.${legal_protection_rcphx ? 'yes' : 'no'}`
        )
      },
      ...arrayConditionnalElement(
        {
          label: 'contracts.form.legal_protection_rcphx_extended',
          content: t(
            `quotes.form.switch.${
              legal_protection_rcphx_extended ? 'yes' : 'no'
            }`
          )
        },
        ['MED', 'OSTEO', 'BIE'].includes(activity_type)
      ),
    ...arrayConditionnalElement(
      {
        label: 'contracts.form.legal_protection_usa_canada',
        content: t(
          `quotes.form.switch.${legal_protection_usa_canada ? 'yes' : 'no'}`
        ),
        span: 2
      },
        !['MED', 'OSTEO', 'BIE'].includes(activity_type)
      ),
      {
        label: 'contracts.form.exploitation_responsibility',
        content: t(
          `quotes.form.switch.${exploitation_responsibility ? 'yes' : 'no'}`
        )
      },
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.form.cyber_option_rcphx',
            content: t(
              `quotes.form.switch.${cyber_option_rcphx ? 'yes' : 'no'}`
            )
          },
          ...arrayConditionnalElement(
            [
              {
                label: 'contracts.form.cyber_option_rcphx_extended',
                content: t(
                  `quotes.form.switch.${cyber_option_rcphx_extended ? 'yes' : 'no'}`
                )
              },
              {
                label: 'contracts.form.cyber_option_rcphx_fraud',
                content: t(
                  `quotes.form.switch.${cyber_option_rcphx_fraud ? 'yes' : 'no'}`
                )
              }
            ],
            !!cyber_option_rcphx
          )
        ],
        RCPHX_CYBER_ALLOWED_ACTIVITIES.includes(activity_type) && turnover < 5_000_000
      )
    ],
    rcngawk: [
      {
        label: 'contracts.form.legal_protection',
        content: t(`quotes.form.switch.${legal_protection ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.extended_legal_protection',
        content: t(
          `quotes.form.switch.${extended_legal_protection ? 'yes' : 'no'}`
        ),
        span: 2
      }
    ],
    rcpcm: [
      {
        label: 'contracts.form.legal_protection',
        content: t(`quotes.form.switch.${legal_protection ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.extended_legal_protection',
        content: t(
          `quotes.form.switch.${extended_legal_protection ? 'yes' : 'no'}`
        ),
        span: 2
      },
      {
        label: 'contracts.form.exploitation_responsibility',
        content: t(
          `quotes.form.switch.${exploitation_responsibility ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.surface_area',
        key: 'surface_area',
        content: surface_area && surface_area
      },
      {
        label: 'contracts.form.social_mandate_responsibility',
        content: t(
          `quotes.form.switch.${social_mandate_responsibility ? 'yes' : 'no'}`
        )
      }
    ],
    rcgls: [
      {
        label: 'contracts.form.legal_protection_option',
        content: t(`quotes.form.switch.${legal_protection ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.extended_legal_protection',
        content: t(
          `quotes.form.switch.${extended_legal_protection ? 'yes' : 'no'}`
        )
      },
      ...(
        {
          COA: [
            'investment_advisor_errors',
            'insurance_intermediary_guarantee'
          ],
          MIA: [
            'insurance_intermediary_guarantee',
            'iobsp_mandate_professional'
          ],
          IOBSP: [
            'insurance_intermediary_guarantee',
            'iobsp_broker_guarantee',
            'banking_solicitor_errors'
          ],
          MIOBSP: [
            'insurance_intermediary_guarantee',
            'insurance_sub_agents_errors'
          ],
          CGP_CIF: ['investment_advisor_errors'],
          AI: [
            'iobsp_broker_errors',
            'iobsp_exclusive_agent_errors',
            'iobsp_non_exclusive_agent_errors',
            'iobsp_mandate_professional',
            'insurance_intermediary_guarantee',
            'general_agents_and_brokers_errors',
            'insurance_sub_agents_errors',
            'real_estate_transaction_guarantee'
          ],
          ACI: []
        }[main_activity] || []
      ).map((key) => ({
        label: `contracts.form.${key}`,
        key,
        content: t(`quotes.form.switch.${contract?.[key] ? 'yes' : 'no'}`)
      }))
    ],
    rcpml: [
      {
        label: 'contracts.form.legal_protection_option',
        content: t(`quotes.form.switch.${legal_protection ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.extended_legal_protection',
        content: t(
          `quotes.form.switch.${extended_legal_protection ? 'yes' : 'no'}`
        ),
        span: 2
      },
      {
        label: 'contracts.form.exploitation_responsibility',
        content: t(
          `quotes.form.switch.${exploitation_responsibility ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.mobility_guarantee',
        content: `${numeral(mobility_guarantee || 0).format('0,0')}€`
      },
      {
        label: 'contracts.form.social_mandate_responsibility',
        content: t(
          `quotes.form.switch.${social_mandate_responsibility ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.key_man_guarantee',
        content: t(`quotes.form.switch.${key_man_guarantee ? 'yes' : 'no'}`)
      }
    ],
    association: [
      ...(local
        ? [
            {
              label: 'contracts.form.local_insurance',
              content: t(`quotes.form.switch.${local_insurance ? 'yes' : 'no'}`)
            },
            {
              label: 'contracts.form.civil_responsibility_asso',
              content: t(
                `quotes.form.switch.${civil_responsibility_asso ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.social_mandate_responsibility',
              content: t(
                `quotes.form.switch.${
                  social_mandate_responsibility ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.classic_accident_insurance',
              content: t(
                `quotes.form.switch.${
                  classic_accident_insurance ? 'yes' : 'no'
                }`
              ),
              span: 2
            },
            {
              label: 'contracts.form.reinforced_accident_insurance',
              content: t(
                `quotes.form.switch.${
                  reinforced_accident_insurance ? 'yes' : 'no'
                }`
              ),
              span: 2
            }
          ]
        : [
            {
              label: 'contracts.form.social_mandate_responsibility',
              content: t(
                `quotes.form.switch.${
                  social_mandate_responsibility ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.classic_accident_insurance',
              content: t(
                `quotes.form.switch.${
                  classic_accident_insurance ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.reinforced_accident_insurance',
              content: t(
                `quotes.form.switch.${
                  reinforced_accident_insurance ? 'yes' : 'no'
                }`
              )
            }
          ])
    ],
    mri: [
      {
        label: 'contracts.form.machine_breakdown',
        content: t(`quotes.form.switch.${machine_breakdown ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.indoor_pool',
        content: t(`quotes.form.switch.${indoor_pool ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.outdoor_pool',
        content: t(`quotes.form.switch.${outdoor_pool ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.solar_panels',
        content: t(`quotes.form.switch.${solar_panels ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.veranda',
        content: t(`quotes.form.switch.${veranda ? 'yes' : 'no'}`),
        span: 2
      },
      {
        label: 'contracts.show.occupation_trade',
        content: t(`contracts.show.${occupation_trade}`)
      },
      ...(occupation_trade !== 'under_25'
        ? [
            {
              label: 'contracts.show.activities_trade',
              content:
                activities_trade &&
                activities_trade.map((element) => {
                  return <p key={element}>{`- ${element}`}</p>;
                }),
              overflow: 'hidden',
              ellipsis: true
            }
          ]
        : [])
    ],
    mriml: [
      {
        label: 'contracts.form.outdoor_indoor_pool',
        content: t(`quotes.form.switch.${outdoor_indoor_pool ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.renewable_energies',
        content: t(`quotes.form.switch.${renewable_energies ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.legal_protection',
        content: t(`quotes.form.switch.${legal_protection ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.show.occupation_trade',
        content: t(`contracts.show.${occupation_trade}`)
      },
      ...(occupation_trade !== 'under_25'
        ? [
            {
              label: 'contracts.show.activities_trade',
              content:
                activities_trade &&
                activities_trade.map((element) => {
                  return <p key={element}>{`- ${element}`}</p>;
                }),
              overflow: 'hidden',
              ellipsis: true
            }
          ]
        : [])
    ],
    pnoml: [
      {
        label: 'contracts.form.furnished_rental_show',
        key: 'furnished_rental',
        content: `${numeral(furnished_rental).format('0,0')}€`
      },
      {
        label: 'contracts.form.squat_option_show',
        key: 'squat_option',
        content: t(`quotes.form.switch.${squat_option ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.legal_protection_option',
        key: 'legal_protection',
        content: t(`quotes.form.switch.${legal_protection ? 'yes' : 'no'}`)
      }
    ],
    do: [
      {
        label: 'contracts.form.employer_protection_short',
        content: t(`quotes.form.switch.${employer_protection ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.cyber_protection_short',
        content: t(`quotes.form.switch.${cyber_protection ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.holding_protection_short',
        content: t(`quotes.form.switch.${holding_protection ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.key_person_protection_short',
        content: t(`quotes.form.switch.${key_person_protection ? 'yes' : 'no'}`)
      }
    ],
    rcpmk: [
      {
        label: 'contracts.form.operational_protection',
        content: t(
          `quotes.form.switch.${operational_protection ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.cyber_events_protection',
        content: t(
          `quotes.form.switch.${cyber_events_protection ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.key_man_protection',
        content: t(`quotes.form.switch.${key_man_protection ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.management_protection',
        content: t(`quotes.form.switch.${management_protection ? 'yes' : 'no'}`)
      },
      ...arrayConditionnalElement(
        {
          label: 'contracts.form.merge_protection',
          content: t(`quotes.form.switch.${merge_protection ? 'yes' : 'no'}`)
        },
        activity_type === 'CONSULTING'
      )
    ],
    mriwk: [
      {
        label: 'contracts.show.mriwk.guarantee_package',
        content: t(
          `contracts.show.mriwk.options.guarantee_package.${contract?.guarantee_package}`
        ),
        span: 3
      },
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.show.mriwk.machine_breakdown',
            content: contract?.optionnal_guarantees?.includes(
              'MACHINE_BREAKDOWN'
            )
              ? t(`quotes.form.switch.yes`)
              : t(`quotes.form.switch.no`),
            span: 1,
            fieldType: 'Boolean',
            defaultValue:
              contract?.optionnal_guarantees?.includes('MACHINE_BREAKDOWN'),
            updater(newValue) {
              const guarantees = (contract?.optionnal_guarantees || []).filter(
                (guarantee) => guarantee !== 'MACHINE_BREAKDOWN'
              );

              if (newValue) {
                guarantees.push('MACHINE_BREAKDOWN');
              }

              return {
                key: 'optionnal_guarantees',
                value: guarantees
              };
            }
          }
        ],
        isWKMri(contract)
      ),
      {
        label: 'contracts.show.mriwk.solar_panels',
        content: contract?.optionnal_guarantees?.includes('SOLAR_PANELS')
          ? t(`quotes.form.switch.yes`)
          : t(`quotes.form.switch.no`),
        span: 1,
        fieldType: 'Boolean',
        defaultValue: contract?.optionnal_guarantees?.includes('SOLAR_PANELS'),
        updater(newValue) {
          const guarantees = (contract?.optionnal_guarantees || []).filter(
            (guarantee) => guarantee !== 'SOLAR_PANELS'
          );

          if (newValue) {
            guarantees.push('SOLAR_PANELS');
          }

          return {
            key: 'optionnal_guarantees',
            value: guarantees
          };
        }
      },
      {
        label: 'contracts.show.mriwk.veranda',
        content: contract?.optionnal_guarantees?.includes('VERANDA')
          ? t(`quotes.form.switch.yes`)
          : t(`quotes.form.switch.no`),
        span: 1,
        fieldType: 'Boolean',
        defaultValue: contract?.optionnal_guarantees?.includes('VERANDA'),
        updater(newValue) {
          const guarantees = (contract?.optionnal_guarantees || []).filter(
            (guarantee) => guarantee !== 'VERANDA'
          );

          if (newValue) {
            guarantees.push('VERANDA');
          }

          return {
            key: 'optionnal_guarantees',
            value: guarantees
          };
        }
      },
      {
        key: 'g1231_property_owner_civil_liability',
        label: 'contracts.show.mriwk.g1231_property_owner_civil_liability',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_property_owner_civil_liability ? 'yes' : 'no'
          }`
        ),
        span: 1
      },
      {
        key: 'g1231_criminal_defense_recourse_following_accident',
        label:
          'contracts.show.mriwk.g1231_criminal_defense_recourse_following_accident',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_criminal_defense_recourse_following_accident
              ? 'yes'
              : 'no'
          }`
        ),
        span: 1
      },
      {
        key: 'g1231_natural_disasters',
        label: 'contracts.show.mriwk.g1231_natural_disasters',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_natural_disasters ? 'yes' : 'no'
          }`
        ),
        span: 1
      },
      {
        key: 'g1231_juridic_protection',
        label: 'contracts.show.mriwk.g1231_juridic_protection',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_juridic_protection ? 'yes' : 'no'
          }`
        ),
        span: 1
      },
      {
        key: 'g1231_technological_risks',
        label: 'contracts.show.mriwk.g1231_technological_risks',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_technological_risks ? 'yes' : 'no'
          }`
        ),
        span: 1
      },
      {
        key: 'g1231_fire_related_risks',
        label: 'contracts.show.mriwk.g1231_fire_related_risks',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_fire_related_risks ? 'yes' : 'no'
          }`
        ),
        span: 1
      },
      {
        key: 'g1231_attacks',
        label: 'contracts.show.mriwk.g1231_attacks',
        content: t(
          `quotes.form.switch.${contract?.g1231_attacks ? 'yes' : 'no'}`
        ),
        span: 1
      },
      {
        key: 'g1231_gareat',
        label: 'contracts.show.mriwk.g1231_gareat',
        content: t(
          `quotes.form.switch.${contract?.g1231_gareat ? 'yes' : 'no'}`
        ),
        span: 1
      },
      {
        key: 'g1231_union_civil_liability',
        label: 'contracts.show.mriwk.g1231_union_civil_liability',
        content: t(
          `quotes.form.switch.${
            contract?.g1231_union_civil_liability ? 'yes' : 'no'
          }`
        ),
        span: 1
      },
      ...arrayConditionnalElement(
        [
          {
            key: 'g0230_storms_hail_heavy_snow',
            label: 'contracts.show.mriwk.g0230_storms_hail_heavy_snow',
            content: t(
              `quotes.form.switch.${
                contract?.g0230_storms_hail_heavy_snow ? 'yes' : 'no'
              }`
            ),
            span: 1
          },
          {
            key: 'g0230_collapse',
            label: 'contracts.show.mriwk.g0230_collapse',
            content: t(
              `quotes.form.switch.${contract?.g0230_collapse ? 'yes' : 'no'}`
            ),
            span: 1
          },
          {
            key: 'g0230_theft',
            label: 'contracts.show.mriwk.g0230_theft',
            content: t(
              `quotes.form.switch.${contract?.g0230_theft ? 'yes' : 'no'}`
            ),
            span: 1
          },
          {
            key: 'g0230_electrical_damage',
            label: 'contracts.show.mriwk.g0230_electrical_damage',
            content: t(
              `quotes.form.switch.${
                contract?.g0230_electrical_damage ? 'yes' : 'no'
              }`
            ),
            span: 1
          }
        ],
        ['PKG_2', 'PKG_3'].includes(contract?.guarantee_package)
      ),
      ...arrayConditionnalElement(
        [
          {
            key: 'g0030_broken_glass',
            label: 'contracts.show.mriwk.g0030_broken_glass',
            content: t(
              `quotes.form.switch.${
                contract?.g0030_broken_glass ? 'yes' : 'no'
              }`
            ),
            span: 1
          },
          {
            key: 'g0030_water_damage',
            label: 'contracts.show.mriwk.g0030_water_damage',
            content: t(
              `quotes.form.switch.${
                contract?.g0030_water_damage ? 'yes' : 'no'
              }`
            ),
            span: 1
          },
          {
            key: 'g0030_vandalism',
            label: 'contracts.show.mriwk.g0030_vandalism',
            content: t(
              `quotes.form.switch.${contract?.g0030_vandalism ? 'yes' : 'no'}`
            ),
            span: 1
          }
        ],
        contract?.guarantee_package === 'PKG_3'
      )
    ]
  };
  const firstStepInfo = {
    rcp: [
      {
        label: 'contracts.form.straw_rubber_activity',
        key: 'straw_rubber_activity',
        content: t(`quotes.form.switch.${straw_rubber_activity ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.industrial_activity',
        key: 'industrial_activity',
        content: t(`quotes.form.switch.${industrial_activity ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.religious_pol',
        key: 'religious_pol',
        content: t(`quotes.form.switch.${religious_pol ? 'yes' : 'no'}`)
      }
    ],
    rcngawk: [
      {
        label: 'contracts.form.withdrawal_drivin_license',
        key: 'withdrawal_drivin_license',
        content: t(
          `quotes.form.switch.${withdrawal_drivin_license ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.liquidation_property_or_legal_recovery',
        key: 'liquidation_property_or_legal_recovery',
        content: t(
          `quotes.form.switch.${
            liquidation_property_or_legal_recovery ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.guaranteed_liability_involved',
        key: 'guaranteed_liability_involved',
        content: t(
          `quotes.form.switch.${guaranteed_liability_involved ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.more_than_two_claims',
        key: 'more_than_two_claims',
        content: t(`quotes.form.switch.${more_than_two_claims ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.compliant_installations',
        key: 'compliant_installations',
        content: t(
          `quotes.form.switch.${compliant_installations ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.bodily_claim',
        key: 'bodily_claim',
        content: t(`quotes.form.switch.${bodily_claim ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.driving_while_intoxicated',
        key: 'driving_while_intoxicated',
        content: t(
          `quotes.form.switch.${driving_while_intoxicated ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.political_person_exposed',
        key: 'political_person_exposed',
        content: t(
          `quotes.form.switch.${political_person_exposed ? 'yes' : 'no'}`
        )
      }
    ],
    rcpwk: [
      {
        label: 'contracts.form.metropolitan_france',
        key: 'metropolitan_france',
        content: t(`quotes.form.switch.${metropolitan_france ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.straw_rubber_activity',
        key: 'straw_rubber_activity',
        content: t(`quotes.form.switch.${straw_rubber_activity ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.industrial_activity',
        key: 'industrial_activity',
        content: t(`quotes.form.switch.${industrial_activity ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.religious_pol',
        key: 'religious_pol',
        content: t(`quotes.form.switch.${religious_pol ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.combustible_packaging',
        content: t(`quotes.form.switch.${combustible_packaging ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.wholesale_business',
        content: t(`quotes.form.switch.${wholesale_business ? 'yes' : 'no'}`)
      }
    ],
    rcpml: [
      {
        label: 'contracts.form.industrial_straw_activity',
        key: 'industrial_straw_activity',
        content: t(
          `quotes.form.switch.${industrial_straw_activity ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.construction_activity',
        key: 'construction_activity',
        content: t(`quotes.form.switch.${construction_activity ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.medical_activity',
        key: 'medical_activity',
        content: t(`quotes.form.switch.${medical_activity ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.legal_activity',
        key: 'legal_activity',
        content: t(`quotes.form.switch.${legal_activity ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.religious_pol',
        key: 'religious_pol',
        content: t(`quotes.form.switch.${religious_pol ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.has_already_been_terminated_by_asx',
        content: t(
          `quotes.form.switch.${
            has_already_been_terminated_by_asx ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.france_with_corsica',
        content: t(`quotes.form.switch.${france_with_corsica ? 'yes' : 'no'}`)
      }
    ],
    rcphx: [
      ...arrayConditionnalElement(
        {
          label: `contracts.form.${getInsuranceHistoricLabel(activity_type)}`,
          key: 'insurance_historic_hiscox',
          content: t(
            `quotes.form.switch.${insurance_historic_hiscox ? 'yes' : 'no'}`
          ),
          span: 3
        },
        !['MED', 'OSTEO', 'BIE'].includes(activity_type)
      ),

      ...getActivityPrerequisites(contract).map((prerequisite) => ({
        label: `contracts.form.${prerequisite.label || prerequisite.name}`,
        key: prerequisite.name,
        span: 3,
        content: t(
          `quotes.form.switch.${contract[prerequisite.name] ? 'yes' : 'no'}`
        )
      })),

      // RISK
      {
        label: 'contracts.form.history_risk_hiscox',
        key: 'history_risk_hiscox',
        content: t(`quotes.form.switch.${history_risk_hiscox ? 'yes' : 'no'}`),
        span: 3
      }
    ],
    rcpcm: [
      {
        label: 'contracts.form.straw_rubber_activity',
        key: 'straw_rubber_activity',
        content: t(`quotes.form.switch.${straw_rubber_activity ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.industrial_activity',
        key: 'industrial_activity',
        content: t(`quotes.form.switch.${industrial_activity ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.religious_pol',
        key: 'religious_pol',
        content: t(`quotes.form.switch.${religious_pol ? 'yes' : 'no'}`)
      }
    ],
    rcgls: [
      {
        label: 'contracts.form.no_activity_usa_canada',
        key: 'no_activity_usa_canada',
        content: t(
          `quotes.form.switch.${no_activity_usa_canada ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.not_listed_in_eu_sanctions',
        key: 'not_listed_in_eu_sanctions',
        content: t(
          `quotes.form.switch.${not_listed_in_eu_sanctions ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.accurate_and_truthful_declarations',
        key: 'accurate_and_truthful_declarations',
        content: t(
          `quotes.form.switch.${
            accurate_and_truthful_declarations ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.report_change_of_situation',
        key: 'report_change_of_situation',
        content: t(
          `quotes.form.switch.${report_change_of_situation ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.no_acpr_sanctions',
        key: 'no_acpr_sanctions',
        content: t(`quotes.form.switch.${no_acpr_sanctions ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.no_foreign_branch_or_subsidiary',
        key: 'no_foreign_branch_or_subsidiary',
        content: t(
          `quotes.form.switch.${no_foreign_branch_or_subsidiary ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.no_problems_last_5_years',
        key: 'no_problems_last_5_years',
        content: t(
          `quotes.form.switch.${no_problems_last_5_years ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.no_known_risk_facts',
        key: 'no_known_risk_facts',
        content: t(`quotes.form.switch.${no_known_risk_facts ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.orias_required_for_guarantee',
        key: 'orias_required_for_guarantee',
        content: t(
          `quotes.form.switch.${orias_required_for_guarantee ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.registration_required_for_guarantee',
        key: 'registration_required_for_guarantee',
        content: t(
          `quotes.form.switch.${
            registration_required_for_guarantee ? 'yes' : 'no'
          }`
        )
      },
      {
        label: 'contracts.form.already_resiliated_by_assurmax',
        key: 'already_resiliated_by_assurmax',
        content: t(
          `quotes.form.switch.${already_resiliated_by_assurmax ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.politically_exposed_person',
        key: 'politically_exposed_person',
        content: t(
          `quotes.form.switch.${politically_exposed_person ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.legal_and_regulatory_compliance',
        key: 'legal_and_regulatory_compliance',
        content: t(
          `quotes.form.switch.${legal_and_regulatory_compliance ? 'yes' : 'no'}`
        )
      },
      // ACI
      ...(main_activity === 'ACI'
        ? [
            {
              label: 'contracts.form.multiple_principals',
              key: 'multiple_principals',
              content: t(
                `quotes.form.switch.${multiple_principals ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.registered_commercial_agent',
              key: 'registered_commercial_agent',
              content: t(
                `quotes.form.switch.${
                  registered_commercial_agent ? 'yes' : 'no'
                }`
              )
            },
            ...(registered_commercial_agent
              ? [
                  {
                    label: 'contracts.form.professional_card_number',
                    key: 'professional_card_number',
                    content: professional_card_number
                  }
                ]
              : [
                  {
                    label: 'contracts.form.in_process_cci',
                    key: 'in_process_cci',
                    content: t(
                      `quotes.form.switch.${in_process_cci ? 'yes' : 'no'}`
                    )
                  }
                ]),
            {
              label: 'contracts.form.issuing_chamber',
              key: 'issuing_chamber',
              content: issuing_chamber
            },
            {
              label: 'contracts.form.agency_contact_details',
              key: 'agency_contact_details',
              content: agency_contact_details
            }
          ]
        : []),

      // CGP_CIF
      ...(main_activity === 'CGP_CIF'
        ? [
            {
              label: 'contracts.form.no_financial_engineering',
              key: 'no_financial_engineering',
              content: t(
                `quotes.form.switch.${no_financial_engineering ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.real_estate_tax_reduction_activity',
              key: 'real_estate_tax_reduction_activity',
              content: t(
                `quotes.form.switch.${
                  real_estate_tax_reduction_activity ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.no_asset_management_for_third_parties',
              key: 'no_asset_management_for_third_parties',
              content: t(
                `quotes.form.switch.${
                  no_asset_management_for_third_parties ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.no_miscellaneous_goods_intermediation',
              key: 'no_miscellaneous_goods_intermediation',
              content: t(
                `quotes.form.switch.${
                  no_miscellaneous_goods_intermediation ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.no_gestion_patrimoine_without_cif',
              key: 'no_gestion_patrimoine_without_cif',
              content: t(
                `quotes.form.switch.${
                  no_gestion_patrimoine_without_cif ? 'yes' : 'no'
                }`
              )
            }
          ]
        : []),

      // IOBSP
      ...(main_activity === 'IOBSP'
        ? [
            {
              label: 'contracts.form.mandataires_to_cover',
              key: 'mandataires_to_cover',
              content: t(
                `quotes.form.switch.${mandataires_to_cover ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.orias_compliance_insurance',
              key: 'orias_compliance_insurance',
              content: t(
                `quotes.form.switch.${
                  orias_compliance_insurance ? 'yes' : 'no'
                }`
              )
            },
            ...(orias_compliance_insurance
              ? [
                  {
                    label: 'contracts.form.orias_number',
                    key: 'orias_number',
                    content: orias_number
                  }
                ]
              : []),
            {
              label: 'contracts.form.accessory_activity_ia_mia_dfb',
              key: 'accessory_activity_ia_mia_dfb',
              content: t(
                `quotes.form.switch.${
                  accessory_activity_ia_mia_dfb ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.accessory_activity_ia_mia',
              key: 'accessory_activity_ia_mia',
              content: t(
                `quotes.form.switch.${accessory_activity_ia_mia ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.accessory_activity_dfb',
              key: 'accessory_activity_dfb',
              content: t(
                `quotes.form.switch.${accessory_activity_dfb ? 'yes' : 'no'}`
              )
            }
          ]
        : []),

      // COA
      ...(main_activity === 'COA'
        ? [
            {
              label: 'contracts.form.delegation_or_mandate',
              key: 'delegation_or_mandate',
              content: t(
                `quotes.form.switch.${delegation_or_mandate ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.accounts_verified_by_accountant',
              key: 'accounts_verified_by_accountant',
              content: t(
                `quotes.form.switch.${
                  accounts_verified_by_accountant ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.mandataires_to_cover_mias',
              key: 'mandataires_to_cover_mias',
              content: t(
                `quotes.form.switch.${mandataires_to_cover_mias ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.no_risky_insurance_contexts',
              key: 'no_risky_insurance_contexts',
              content: t(
                `quotes.form.switch.${
                  no_risky_insurance_contexts ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.reinsurance_broker',
              key: 'reinsurance_broker',
              content: t(
                `quotes.form.switch.${reinsurance_broker ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.orias_compliance_assurance',
              key: 'orias_compliance_assurance',
              content: t(
                `quotes.form.switch.${
                  orias_compliance_assurance ? 'yes' : 'no'
                }`
              )
            },
            ...(orias_compliance_assurance
              ? [
                  {
                    label: 'contracts.form.orias_number',
                    key: 'orias_number',
                    content: orias_number
                  }
                ]
              : []),
            {
              label: 'contracts.form.accessory_activity_iobsp_dfb_cif',
              key: 'accessory_activity_iobsp_dfb_cif',
              content: t(
                `quotes.form.switch.${
                  accessory_activity_iobsp_dfb_cif ? 'yes' : 'no'
                }`
              )
            },
            ...(accessory_activity_iobsp_dfb_cif
              ? [
                  {
                    label: 'contracts.form.accessory_activity_iobsp',
                    key: 'accessory_activity_iobsp',
                    content: t(
                      `quotes.form.switch.${
                        accessory_activity_iobsp ? 'yes' : 'no'
                      }`
                    )
                  },
                  {
                    label: 'contracts.form.accessory_activity_dfb',
                    key: 'accessory_activity_dfb',
                    content: t(
                      `quotes.form.switch.${
                        accessory_activity_dfb ? 'yes' : 'no'
                      }`
                    )
                  },
                  {
                    label: 'contracts.form.accessory_activity_cif',
                    key: 'accessory_activity_cif',
                    content: t(
                      `quotes.form.switch.${
                        accessory_activity_cif ? 'yes' : 'no'
                      }`
                    )
                  }
                ]
              : [])
          ]
        : []),

      // AI
      ...(main_activity === 'AI'
        ? [
            {
              label: 'contracts.form.no_foreign_branch',
              key: 'no_foreign_branch',
              content: t(
                `quotes.form.switch.${no_foreign_branch ? 'yes' : 'no'}`
              )
            },

            ...(no_foreign_branch
              ? [
                  {
                    label: 'contracts.form.legal_representative_name',
                    key: 'legal_representative_name',
                    content: legal_representative_name
                  }
                ]
              : []),
            {
              label: 'contracts.form.no_property_management_activity',
              key: 'no_property_management_activity',
              content: t(
                `quotes.form.switch.${
                  no_property_management_activity ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.real_estate_tax_reduction_activity_ai',
              key: 'real_estate_tax_reduction_activity_ai',
              content: t(
                `quotes.form.switch.${
                  real_estate_tax_reduction_activity_ai ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.wants_financial_guarantee_no_funds',
              key: 'wants_financial_guarantee_no_funds',
              content: t(
                `quotes.form.switch.${
                  wants_financial_guarantee_no_funds ? 'yes' : 'no'
                }`
              )
            },
            ...(wants_financial_guarantee_no_funds
              ? [
                  {
                    label: 'contracts.form.financial_guarantee_amount_no_funds',
                    key: 'financial_guarantee_amount_no_funds',
                    content: financial_guarantee_amount_no_funds
                  }
                ]
              : []),
            {
              label: 'contracts.form.accessory_activity_ia_mia_or_iobsp_miobsp',
              key: 'accessory_activity_ia_mia_or_iobsp_miobsp',
              content: t(
                `quotes.form.switch.${
                  accessory_activity_ia_mia_or_iobsp_miobsp ? 'yes' : 'no'
                }`
              )
            },
            ...(accessory_activity_ia_mia_or_iobsp_miobsp
              ? [
                  {
                    label: 'contracts.form.accessory_activity_ia_mia_alt',
                    key: 'accessory_activity_ia_mia_alt',
                    content: t(
                      `quotes.form.switch.${
                        accessory_activity_ia_mia_alt ? 'yes' : 'no'
                      }`
                    )
                  },
                  {
                    label: 'contracts.form.accessory_activity_iobsp_miobsp',
                    key: 'accessory_activity_iobsp_miobsp',
                    content: t(
                      `quotes.form.switch.${
                        accessory_activity_iobsp_miobsp ? 'yes' : 'no'
                      }`
                    )
                  }
                ]
              : [])
          ]
        : []),
      // MIA

      ...(main_activity === 'MIOBSP'
        ? [
            {
              label: 'contracts.form.single_mandate_iobsp',
              key: 'single_mandate_iobsp',
              content: t(
                `quotes.form.switch.${single_mandate_iobsp ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.mandant_details',
              key: 'mandant_details',
              content: mandant_details
            },
            {
              label: 'contracts.form.orias_compliance_miobsp',
              key: 'orias_compliance_miobsp',
              content: t(
                `quotes.form.switch.${orias_compliance_miobsp ? 'yes' : 'no'}`
              )
            },
            ...(orias_compliance_miobsp
              ? [
                  {
                    label: 'contracts.form.orias_number',
                    key: 'orias_number',
                    content: orias_number
                  }
                ]
              : []),
            {
              label: 'contracts.form.only_france_based_clients',
              key: 'only_france_based_clients',
              content: t(
                `quotes.form.switch.${only_france_based_clients ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.no_us_canada_bank_activity',
              key: 'no_us_canada_bank_activity',
              content: t(
                `quotes.form.switch.${
                  no_us_canada_bank_activity ? 'yes' : 'no'
                }`
              )
            }
          ]
        : []),

      ...(main_activity === 'MIA'
        ? [
            {
              label: 'contracts.form.single_mandate_insurance',
              key: 'single_mandate_insurance',
              content: t(
                `quotes.form.switch.${single_mandate_insurance ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.orias_compliance_bank',
              key: 'orias_compliance_bank',
              content: t(
                `quotes.form.switch.${orias_compliance_bank ? 'yes' : 'no'}`
              )
            },
            ...(orias_compliance_bank
              ? [
                  {
                    label: 'contracts.form.orias_number',
                    key: 'orias_number',
                    content: orias_number
                  }
                ]
              : []),
            {
              label: 'contracts.form.accounts_verified_by_accountant_mia',
              key: 'accounts_verified_by_accountant_mia',
              content: t(
                `quotes.form.switch.${
                  accounts_verified_by_accountant_mia ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.only_france_based_clients',
              key: 'only_france_based_clients',
              content: t(
                `quotes.form.switch.${only_france_based_clients ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.risky_mandant_relationships',
              key: 'risky_mandant_relationships',
              content: t(
                `quotes.form.switch.${
                  risky_mandant_relationships ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.maritime_insurance',
              key: 'maritime_insurance',
              content: t(
                `quotes.form.switch.${maritime_insurance ? 'yes' : 'no'}`
              )
            },
            ...(maritime_insurance
              ? [
                  {
                    label: 'contracts.form.maritime_percentage',
                    key: 'maritime_percentage',
                    content: maritime_percentage
                  }
                ]
              : []),
            {
              label: 'contracts.form.construction_insurance',
              key: 'construction_insurance',
              content: t(
                `quotes.form.switch.${construction_insurance ? 'yes' : 'no'}`
              )
            },
            ...(construction_insurance
              ? [
                  {
                    label:
                      'contracts.form.construction_insurance_company_check',
                    key: 'construction_insurance_company_check',
                    content: t(
                      `quotes.form.switch.${
                        construction_insurance_company_check ? 'yes' : 'no'
                      }`
                    )
                  },
                  {
                    label: 'contracts.form.construction_project_check',
                    key: 'construction_project_check',
                    content: t(
                      `quotes.form.switch.${
                        construction_project_check ? 'yes' : 'no'
                      }`
                    )
                  },
                  {
                    label: 'contracts.form.construction_revenue_percentage',
                    key: 'construction_revenue_percentage',
                    content: construction_revenue_percentage
                  }
                ]
              : [])
          ]
        : [])
    ],
    association: [
      {
        label: 'contracts.form.litigation_responsibility',
        key: 'litigation_responsibility',
        content: t(
          `quotes.form.switch.${litigation_responsibility ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.annual_budget',
        key: 'annual_budget',
        content: t(`quotes.form.switch.${annual_budget ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.political_activity',
        key: 'political_activity',
        content: t(`quotes.form.switch.${political_activity ? 'yes' : 'no'}`)
      },
      ...(termination_reason_by_company === 'TERMINATION_NO_PAYMENT'
        ? [
            {
              label: 'contracts.form.no_claims',
              key: 'no_claims',
              content: t(`quotes.form.switch.${no_claims ? 'yes' : 'no'}`)
            }
          ]
        : [
            {
              label: 'contracts.form.max_claims',
              key: 'max_claims',
              content: t(`quotes.form.switch.${max_claims ? 'yes' : 'no'}`)
            }
          ]),
      {
        label: 'contracts.form.blocked_activities',
        key: 'blocked_activities',
        content: t(`quotes.form.switch.${blocked_activities ? 'yes' : 'no'}`)
      },
      ...(local
        ? [
            {
              label: 'contracts.form.is_a_palace',
              key: 'is_a_palace',
              content: t(`quotes.form.switch.${is_a_palace ? 'yes' : 'no'}`)
            },
            {
              label: 'contracts.form.mall',
              key: 'mall',
              content: t(`quotes.form.switch.${mall ? 'yes' : 'no'}`)
            },
            {
              label: 'contracts.form.monument',
              key: 'monument',
              content: t(`quotes.form.switch.${monument ? 'yes' : 'no'}`)
            },
            {
              label: 'contracts.form.closed_more_60_days',
              key: 'closed_more_60_days',
              content: t(
                `quotes.form.switch.${closed_more_60_days ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.embassy_bowling_disco',
              key: 'embassy_bowling_disco',
              content: t(
                `quotes.form.switch.${embassy_bowling_disco ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.industry_less_10m',
              key: 'industry_less_10m',
              content: t(
                `quotes.form.switch.${industry_less_10m ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.close_help',
              key: 'close_help',
              content: t(`quotes.form.switch.${close_help ? 'yes' : 'no'}`)
            },
            {
              label: 'contracts.form.altitude_location',
              key: 'altitude_location',
              content: t(
                `quotes.form.switch.${altitude_location ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.renovation',
              key: 'altituderenovation_location',
              content: t(`quotes.form.switch.${renovation ? 'yes' : 'no'}`)
            },
            {
              label: 'contracts.form.had_waived_certain_remedies_in_its_lease',
              key: 'had_waived_certain_remedies_in_its_lease',
              content: t(
                `quotes.form.switch.${
                  had_waived_certain_remedies_in_its_lease ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.metropolitan_france',
              key: 'metropolitan_france',
              content: t(
                `quotes.form.switch.${metropolitan_france ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.built_covered_hard_materials',
              key: 'built_covered_hard_materials',
              content: t(
                `quotes.form.switch.${
                  built_covered_hard_materials ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.disaffected',
              key: 'disaffected',
              content: t(`quotes.form.switch.${disaffected ? 'yes' : 'no'}`)
            },
            {
              label: 'contracts.form.zone_ind_com_art',
              key: 'zone_ind_com_art',
              content: t(
                `quotes.form.switch.${zone_ind_com_art ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.built_covered_hard_materials',
              key: 'built_covered_hard_materials',
              content: t(
                `quotes.form.switch.${
                  built_covered_hard_materials ? 'yes' : 'no'
                }`
              )
            },
            {
              label: 'contracts.form.take_care_building',
              key: 'take_care_building',
              content: t(
                `quotes.form.switch.${take_care_building ? 'yes' : 'no'}`
              )
            },
            {
              label: 'contracts.form.extinguisher_conformed',
              key: 'extinguisher_conformed',
              content: t(
                `quotes.form.switch.${extinguisher_conformed ? 'yes' : 'no'}`
              )
            }
          ]
        : [])
    ],
    mrpwk: [
      {
        label: 'contracts.form.75percent_tough_materials',
        content: t(`quotes.form.switch.${tough_materials ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.take_care_building',
        content: t(`quotes.form.switch.${take_care_building ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.2_incidents_max',
        content: t(`quotes.form.switch.${incidents_max ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.more_500m_altitude',
        content: t(`quotes.form.switch.${more_500m_altitude ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.close_help',
        content: t(`quotes.form.switch.${close_help ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.castle',
        content: t(`quotes.form.switch.${castle ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.monument_outside_prerequisites',
        content: t(`quotes.form.switch.${monument ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.ffb_1000_times',
        content: t(`quotes.form.switch.${ffb_1000_times ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.closed_more_60_days',
        content: t(`quotes.form.switch.${closed_more_60_days ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.mall',
        content: t(`quotes.form.switch.${mall ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.embassy_bowling_disco_wk',
        content: t(
          `quotes.form.switch.${embassy_bowling_disco ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.religious_political',
        content: t(`quotes.form.switch.${religious_political ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.industry_less_10m',
        content: t(`quotes.form.switch.${industry_less_10m ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.wood_plastic_nuclear_activity',
        content: t(
          `quotes.form.switch.${wood_plastic_nuclear_activity ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.straw_rubber_stock',
        content: t(`quotes.form.switch.${straw_rubber_stock ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.walking_cooler_wk',
        content: t(`quotes.form.switch.${walking_cooler ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.extinguisher_conformed',
        content: t(
          `quotes.form.switch.${extinguisher_conformed ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.electric_conformed',
        content: t(`quotes.form.switch.${electric_conformed ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.restaurant_activity',
        content: t(`quotes.form.switch.${restaurant_activity ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.chilled_products_conformed',
        content: t(
          `quotes.form.switch.${chilled_products_conformed ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.disaffected',
        content: t(`quotes.form.switch.${disaffected ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.tags_roof_materials',
        content: t(`quotes.form.switch.${tags_roof_materials ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.renovation_wk',
        content: t(`quotes.form.switch.${renovation ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.28m_high_wk',
        content: t(`quotes.form.switch.${m_high ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.zone_ind_com_art',
        content: t(`quotes.form.switch.${zone_ind_com_art ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.sea_side',
        content: t(`quotes.form.switch.${sea_side ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.combustible_packaging',
        content: t(`quotes.form.switch.${combustible_packaging ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.wholesale_business',
        content: t(`quotes.form.switch.${wholesale_business ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.close_before_2300',
        content: t(`quotes.form.switch.${close_before_2300 ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.metropolitan_france',
        content: t(`quotes.form.switch.${metropolitan_france ? 'yes' : 'no'}`)
      }
    ],
    mrpml: [
      {
        label: 'contracts.form.75percent_tough_materials',
        content: t(`quotes.form.switch.${tough_materials ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.90percent_tough_materials',
        content: t(
          `quotes.form.switch.${percent_tough_materials ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.take_care_building',
        content: t(`quotes.form.switch.${take_care_building ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.more_500m_altitude',
        content: t(`quotes.form.switch.${more_500m_altitude ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.close_help',
        content: t(`quotes.form.switch.${close_help ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.metropolitan_france',
        content: t(`quotes.form.switch.${metropolitan_france ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.half_timbering_structure',
        content: t(
          `quotes.form.switch.${half_timbering_structure ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.closed_more_60_days',
        content: t(`quotes.form.switch.${closed_more_60_days ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.monument',
        content: t(`quotes.form.switch.${monument ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.ffb_1000_times',
        content: t(`quotes.form.switch.${ffb_1000_times ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.embassy_bowling_disco',
        content: t(
          `quotes.form.switch.${embassy_bowling_disco ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.religious_political',
        content: t(`quotes.form.switch.${religious_political ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.industry_less_10m',
        content: t(`quotes.form.switch.${industry_less_10m ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.zone_ind_com_art',
        content: t(`quotes.form.switch.${zone_ind_com_art ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.disaffected',
        content: t(`quotes.form.switch.${disaffected ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.walking_cooler',
        content: t(`quotes.form.switch.${walking_cooler ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.renovation',
        content: t(`quotes.form.switch.${renovation ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.28m_high',
        content: t(`quotes.form.switch.${m_high ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.tags_roof_materials',
        content: t(`quotes.form.switch.${tags_roof_materials ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.risk_has_graffitis_or_tags',
        content: t(
          `quotes.form.switch.${risk_has_graffitis_or_tags ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.roof_non_standard_materials',
        content: t(
          `quotes.form.switch.${roof_non_standard_materials ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.roof_built_or_replaced_after_2018',
        content: t(
          `quotes.form.switch.${roof_built_or_replaced_after_2018 ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.roof_is_flat_or_terrace',
        content: t(
          `quotes.form.switch.${roof_is_flat_or_terrace ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.industrial_straw_activity',
        content: t(
          `quotes.form.switch.${industrial_straw_activity ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.construction_activity',
        content: t(
          `quotes.form.switch.${construction_activity ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_declared_is_a_castle',
        content: t(
          `quotes.form.switch.${risk_declared_is_a_castle ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.localized_qpv',
        content: t(`quotes.form.switch.${localized_qpv ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.has_already_been_terminated_by_asx',
        content: t(
          `quotes.form.switch.${
            has_already_been_terminated_by_asx ? 'yes' : 'no'
          }`
        ),
        span: 3
      }
    ],
    mrpcm: [
      {
        label: 'contracts.form.75percent_tough_materials',
        content: t(`quotes.form.switch.${tough_materials ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.take_care_building',
        content: t(`quotes.form.switch.${take_care_building ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.2_incidents_max',
        content: t(`quotes.form.switch.${incidents_max ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.more_500m_altitude',
        content: t(`quotes.form.switch.${more_500m_altitude ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.close_help',
        content: t(`quotes.form.switch.${close_help ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.castle',
        content: t(`quotes.form.switch.${castle ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.monument',
        content: t(`quotes.form.switch.${monument ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.liquidation',
        content: t(`quotes.form.switch.${liquidation ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.ffb_1000_times',
        content: t(`quotes.form.switch.${ffb_1000_times ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.closed_more_60_days',
        content: t(`quotes.form.switch.${closed_more_60_days ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.mall',
        content: t(`quotes.form.switch.${mall ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.embassy_bowling_disco_wk',
        content: t(
          `quotes.form.switch.${embassy_bowling_disco ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.religious_political',
        content: t(`quotes.form.switch.${religious_political ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.industry_less_10m',
        content: t(`quotes.form.switch.${industry_less_10m ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.wood_plastic_nuclear_activity',
        content: t(
          `quotes.form.switch.${wood_plastic_nuclear_activity ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.straw_rubber_stock',
        content: t(`quotes.form.switch.${straw_rubber_stock ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.walking_cooler_wk',
        content: t(`quotes.form.switch.${walking_cooler ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.extinguisher_conformed',
        content: t(
          `quotes.form.switch.${extinguisher_conformed ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.electric_conformed',
        content: t(`quotes.form.switch.${electric_conformed ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.restaurant_activity',
        content: t(`quotes.form.switch.${restaurant_activity ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.chilled_products_conformed',
        content: t(
          `quotes.form.switch.${chilled_products_conformed ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.disaffected',
        content: t(`quotes.form.switch.${disaffected ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.tags_roof_materials',
        content: t(`quotes.form.switch.${tags_roof_materials ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.renovation_wk',
        content: t(`quotes.form.switch.${renovation ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.28m_high_wk',
        content: t(`quotes.form.switch.${m_high ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.sea_side',
        content: t(`quotes.form.switch.${sea_side ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.close_before_2300',
        content: t(`quotes.form.switch.${close_before_2300 ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.metropolitan_france',
        content: t(`quotes.form.switch.${metropolitan_france ? 'yes' : 'no'}`)
      }
    ],
    mrp: [
      {
        label: 'contracts.form.75percent_tough_materials',
        content: t(`quotes.form.switch.${tough_materials ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.take_care_building',
        content: t(`quotes.form.switch.${take_care_building ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.2_incidents_max',
        content: t(`quotes.form.switch.${incidents_max ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.more_500m_altitude',
        content: t(`quotes.form.switch.${more_500m_altitude ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.close_help',
        content: t(`quotes.form.switch.${close_help ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.castle',
        content: t(`quotes.form.switch.${castle ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.monument',
        content: t(`quotes.form.switch.${monument ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.liquidation',
        content: t(`quotes.form.switch.${liquidation ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.ffb_1000_times',
        content: t(`quotes.form.switch.${ffb_1000_times ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.closed_more_60_days',
        content: t(`quotes.form.switch.${closed_more_60_days ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.mall',
        content: t(`quotes.form.switch.${mall ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.embassy_bowling_disco',
        content: t(
          `quotes.form.switch.${embassy_bowling_disco ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.religious_political',
        content: t(`quotes.form.switch.${religious_political ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.industry_less_10m',
        content: t(`quotes.form.switch.${industry_less_10m ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.wood_plastic_nuclear_activity',
        content: t(
          `quotes.form.switch.${wood_plastic_nuclear_activity ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.straw_rubber_stock',
        content: t(`quotes.form.switch.${straw_rubber_stock ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.walking_cooler',
        content: t(`quotes.form.switch.${walking_cooler ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.extinguisher_conformed',
        content: t(
          `quotes.form.switch.${extinguisher_conformed ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.electric_conformed',
        content: t(`quotes.form.switch.${electric_conformed ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.restaurant_activity',
        content: t(`quotes.form.switch.${restaurant_activity ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.chilled_products_conformed',
        content: t(
          `quotes.form.switch.${chilled_products_conformed ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.disaffected',
        content: t(`quotes.form.switch.${disaffected ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.tags_roof_materials',
        content: t(`quotes.form.switch.${tags_roof_materials ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.renovation',
        content: t(`quotes.form.switch.${renovation ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.28m_high',
        content: t(`quotes.form.switch.${m_high ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.sea_side',
        content: t(`quotes.form.switch.${sea_side ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.close_before_2300',
        content: t(`quotes.form.switch.${close_before_2300 ? 'yes' : 'no'}`)
      },
      {
        label: 'contracts.form.metropolitan_france',
        content: t(`quotes.form.switch.${metropolitan_france ? 'yes' : 'no'}`)
      }
    ],
    mri: [
      {
        label: 'contracts.form.hard_materials',
        content: t(`quotes.form.switch.${hard_materials ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.good_state',
        content: t(`quotes.form.switch.${good_state ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.altitude_location',
        content: t(`quotes.form.switch.${altitude_location ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.accessibility',
        content: t(`quotes.form.switch.${accessibility ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.metropolitan_france',
        content: t(`quotes.form.switch.${metropolitan_france ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.activity_open_public',
        content: t(`quotes.form.switch.${activity_open_public ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.contractual_general_indemnity_limit',
        content: t(
          `quotes.form.switch.${
            contractual_general_indemnity_limit ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.castle_manor',
        content: t(`quotes.form.switch.${castle_manor ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.classified_listed',
        content: t(`quotes.form.switch.${classified_listed ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.redress_liquidation',
        content: t(`quotes.form.switch.${redress_liquidation ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.wooden_construction',
        content: t(`quotes.form.switch.${wooden_construction ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.temporary_activity',
        content: t(`quotes.form.switch.${temporary_activity ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.specific1_activities',
        content: t(`quotes.form.switch.${specific1_activities ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.religious_political_union',
        content: t(
          `quotes.form.switch.${religious_political_union ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.close_industrial_complex',
        content: t(
          `quotes.form.switch.${close_industrial_complex ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.specific2_activities',
        content: t(`quotes.form.switch.${specific2_activities ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.specific3_activities',
        content: t(`quotes.form.switch.${specific3_activities ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.waiver_recourse_against_tenants',
        content: t(
          `quotes.form.switch.${waiver_recourse_against_tenants ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.cold_rooms_flammable_liquids',
        content: t(
          `quotes.form.switch.${cold_rooms_flammable_liquids ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.land_classified_non_constructible',
        content: t(
          `quotes.form.switch.${
            land_classified_non_constructible ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.building_height',
        content: t(`quotes.form.switch.${building_height ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.tags_roof_materials',
        content: t(`quotes.form.switch.${tags_roof_materials ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.agricultural_building',
        content: t(
          `quotes.form.switch.${agricultural_building ? 'yes' : 'no'}`
        ),
        span: 3
      }
    ],
    mriml: [
      {
        label:
          'contracts.form.risk_assured_location_outside_metropolitan_france',
        content: t(
          `quotes.form.switch.${
            risk_assured_location_outside_metropolitan_france ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.activity_in_qpv_area',
        content: t(`quotes.form.switch.${activity_in_qpv_area ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.risk_under_construction_or_90_percent_materials',
        content: t(
          `quotes.form.switch.${
            risk_under_construction_or_90_percent_materials ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_is_for_depollution_rehabilitation',
        content: t(
          `quotes.form.switch.${
            risk_is_for_depollution_rehabilitation ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_includes_veranda_over_3m2',
        content: t(
          `quotes.form.switch.${risk_includes_veranda_over_3m2 ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_is_wooden_structure_or_wood_cladding',
        content: t(
          `quotes.form.switch.${
            risk_is_wooden_structure_or_wood_cladding ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_is_contiguous_with_industrial_activity',
        content: t(
          `quotes.form.switch.${
            risk_is_contiguous_with_industrial_activity ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label:
          'contracts.form.proximity_of_building_to_risk_of_peril_in_marseille',
        content: t(
          `quotes.form.switch.${
            proximity_of_building_to_risk_of_peril_in_marseille ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_is_occupied_illegally',
        content: t(
          `quotes.form.switch.${risk_is_occupied_illegally ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_is_subject_to_alignment',
        content: t(
          `quotes.form.switch.${risk_is_subject_to_alignment ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_contains_inflammable_liquids',
        content: t(
          `quotes.form.switch.${
            risk_contains_inflammable_liquids ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_is_above_1500m_and_inaccessible_by_rescue',
        content: t(
          `quotes.form.switch.${
            risk_is_above_1500m_and_inaccessible_by_rescue ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label:
          'contracts.form.presence_of_lease_with_waiver_of_recourse_clause',
        content: t(
          `quotes.form.switch.${
            presence_of_lease_with_waiver_of_recourse_clause ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.building_subject_to_major_works_or_renovation',
        content: t(
          `quotes.form.switch.${
            building_subject_to_major_works_or_renovation ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_dedicated_to_demolition',
        content: t(
          `quotes.form.switch.${risk_dedicated_to_demolition ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.establishments_receiving_public_category_1_to_4',
        content: t(
          `quotes.form.switch.${
            establishments_receiving_public_category_1_to_4 ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label:
          'contracts.form.establishments_receiving_public_category_5_closing_before_23h',
        content: t(
          `quotes.form.switch.${
            establishments_receiving_public_category_5_closing_before_23h
              ? 'yes'
              : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_involves_specific_high_risk_activities',
        content: t(
          `quotes.form.switch.${
            risk_involves_specific_high_risk_activities ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_under_judicial_administration',
        content: t(
          `quotes.form.switch.${
            risk_under_judicial_administration ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.roof_non_standard_materials',
        content: t(
          `quotes.form.switch.${roof_non_standard_materials ? 'yes' : 'no'}`
        ),
        isExemption: exemptions?.includes('roof_non_standard_materials'),
        span: 3
      },
      {
        label: 'contracts.form.policyholder_in_bankruptcy_or_liquidation',
        content: t(
          `quotes.form.switch.${
            policyholder_in_bankruptcy_or_liquidation ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_is_vacant_land',
        content: t(`quotes.form.switch.${risk_is_vacant_land ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label:
          'contracts.form.risk_declared_contains_high_risk_activities_or_premises',
        content: t(
          `quotes.form.switch.${
            risk_declared_contains_high_risk_activities_or_premises
              ? 'yes'
              : 'no'
          }`
        ),
        span: 3
      },
      {
        label:
          'contracts.form.risk_contains_religious_political_diplomatic_or_union_activity',
        content: t(
          `quotes.form.switch.${
            risk_contains_religious_political_diplomatic_or_union_activity
              ? 'yes'
              : 'no'
          }`
        ),
        span: 3
      },
      {
        label:
          'contracts.form.risk_declared_is_high_rise_or_large_real_estate_complex',
        content: t(
          `quotes.form.switch.${
            risk_declared_is_high_rise_or_large_real_estate_complex
              ? 'yes'
              : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_declared_is_a_castle',
        content: t(
          `quotes.form.switch.${risk_declared_is_a_castle ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_declared_is_classified_or_listed_building',
        content: t(
          `quotes.form.switch.${
            risk_declared_is_classified_or_listed_building ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_is_an_unbuildable_land',
        content: t(
          `quotes.form.switch.${risk_is_an_unbuildable_land ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_contains_cold_storage_above_120m3',
        content: t(
          `quotes.form.switch.${
            risk_contains_cold_storage_above_120m3 ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_declared_is_in_flood_zone',
        content: t(
          `quotes.form.switch.${risk_declared_is_in_flood_zone ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_is_not_in_good_condition',
        content: t(
          `quotes.form.switch.${risk_is_not_in_good_condition ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.will_property_be_renovated_in_4_months',
        content: t(
          `quotes.form.switch.${
            will_property_be_renovated_in_4_months ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label:
          'contracts.form.will_property_be_renovated_in_more_than_4_months',
        content: t(
          `quotes.form.switch.${
            will_property_be_renovated_in_more_than_4_months ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label:
          'contracts.form.risk_declared_under_unsanitary_or_dangerous_decree',
        content: t(
          `quotes.form.switch.${
            risk_declared_under_unsanitary_or_dangerous_decree ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.is_property_dealer',
        content: t(`quotes.form.switch.${is_property_dealer ? 'yes' : 'no'}`),
        span: 3
      }
    ],
    pnoml: [
      {
        label: 'contracts.form.tenant_in_restructuring_or_liquidation',
        content: t(
          `quotes.form.switch.${
            tenant_in_restructuring_or_liquidation ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.property_under_construction_prerequisite',
        content: t(
          `quotes.form.switch.${
            property_under_construction_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.no_prohibited_activities_declared',
        content: t(
          `quotes.form.switch.${
            no_prohibited_activities_declared ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.property_in_renovation_over_4_months',
        content: t(
          `quotes.form.switch.${
            property_in_renovation_over_4_months ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.property_in_historic_building',
        content: t(
          `quotes.form.switch.${property_in_historic_building ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.property_in_castle_or_listed_building',
        content: t(
          `quotes.form.switch.${property_in_castle_or_listed_building ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_has_prohibited_restaurant_activities',
        content: t(
          `quotes.form.switch.${risk_has_prohibited_restaurant_activities ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.risk_has_industrial_activities',
        content: t(
          `quotes.form.switch.${risk_has_industrial_activities ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.is_property_occupied_or_will_be_within_2_months',
        content: t(
          `quotes.form.switch.${
            is_property_occupied_or_will_be_within_2_months ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.property_previously_insured',
        content: t(
          `quotes.form.switch.${property_previously_insured ? 'yes' : 'no'}`
        ),
        span: 3
      },
      ...(!property_previously_insured
        ? [
            {
              label:
                'contracts.form.have_you_owned_property_for_more_than_2_months',
              content: t(
                `quotes.form.switch.${
                  have_you_owned_property_for_more_than_2_months ? 'yes' : 'no'
                }`
              ),
              span: 3
            }
          ]
        : [])
    ],
    do: [
      {
        label: 'contracts.form.max_tax_result',
        content: t(`quotes.form.switch.${max_tax_result ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.metropolitan_france',
        content: t(`quotes.form.switch.${metropolitan_france ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.more_than_2_years',
        content: t(`quotes.form.switch.${more_than_2_years ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.no_merge_takeover',
        content: t(`quotes.form.switch.${no_merge_takeover ? 'yes' : 'no'}`),
        span: 3
      },
      {
        label: 'contracts.form.stock_exchange_listing',
        content: t(
          `quotes.form.switch.${stock_exchange_listing ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.insolvency_proceeding',
        content: t(
          `quotes.form.switch.${insolvency_proceeding ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.not_operate_in_forbidden_activities',
        content: t(
          `quotes.form.switch.${
            not_operate_in_forbidden_activities ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.positive_last_2_tax_result',
        content: t(
          `quotes.form.switch.${positive_last_2_tax_result ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.positive_last_2_equity',
        content: t(
          `quotes.form.switch.${positive_last_2_equity ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.last_5_years_blame',
        content: t(`quotes.form.switch.${last_5_years_blame ? 'yes' : 'no'}`),
        span: 3
      }
    ],
    rcpmk: [
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.form.it_forbidden_activities',
            content: t(
              `quotes.form.switch.${it_forbidden_activities ? 'yes' : 'no'}`
            ),
            span: 3
          },
          {
            label: 'contracts.form.it_regulated_activities',
            content: t(
              `quotes.form.switch.${it_regulated_activities ? 'yes' : 'no'}`
            ),
            span: 3
          }
        ],
        activity_type === 'IT'
      ),
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.form.media_forbidden_activities',
            content: t(
              `quotes.form.switch.${media_forbidden_activities ? 'yes' : 'no'}`
            ),
            span: 3
          }
        ],
        activity_type === 'MEDIA'
      ),
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.form.service_forbidden_activities_one',
            content: t(
              `quotes.form.switch.${
                service_forbidden_activities_one ? 'yes' : 'no'
              }`
            ),
            span: 3
          },
          {
            label: 'contracts.form.service_forbidden_activities_two',
            content: t(
              `quotes.form.switch.${
                service_forbidden_activities_two ? 'yes' : 'no'
              }`
            ),
            span: 3
          },
          {
            label: 'contracts.form.service_forbidden_activities_three',
            content: t(
              `quotes.form.switch.${
                service_forbidden_activities_three ? 'yes' : 'no'
              }`
            ),
            span: 3
          }
        ],
        activity_type === 'SERVICE'
      ),
      ...arrayConditionnalElement(
        {
          label: 'contracts.form.consulting_forbidden_activities',
          content: t(
            `quotes.form.switch.${
              consulting_forbidden_activities ? 'yes' : 'no'
            }`
          ),
          span: 3
        },
        activity_type === 'CONSULTING'
      ),

      ...arrayConditionnalElement(
        {
          label: 'contracts.form.managment_protection_prerequisite',
          content: t(
            `quotes.form.switch.${
              managment_protection_prerequisite ? 'yes' : 'no'
            }`
          ),
          span: 3
        },
        !!management_protection
      ),
      ...arrayConditionnalElement(
        {
          label: 'contracts.form.cyber_protection_prerequisite',
          content: t(
            `quotes.form.switch.${cyber_protection_prerequisite ? 'yes' : 'no'}`
          ),
          span: 3
        },
        !!cyber_events_protection
      ),
      ...arrayConditionnalElement(
        {
          label: 'contracts.form.merge_protection_prerequisite',
          content: t(
            `quotes.form.switch.${merge_protection_prerequisite ? 'yes' : 'no'}`
          ),
          span: 3
        },
        !!merge_protection
      ),

      {
        label: 'contracts.form.etablishment_outside_france',
        content: t(
          `quotes.form.switch.${etablishment_outside_france ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.limited_turnover_in_usa',
        content: t(
          `quotes.form.switch.${limited_turnover_in_usa ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.limited_claims_indemnity',
        content: t(
          `quotes.form.switch.${limited_claims_indemnity ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.form.current_claim',
        content: t(`quotes.form.switch.${current_claim ? 'yes' : 'no'}`),
        span: 3
      }
    ],
    mriwk: [
      {
        label: 'contracts.show.mriwk.hard_material_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.hard_material_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.good_state_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.good_state_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.firefighting_water_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.firefighting_water_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.rescue_access_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.rescue_access_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.metropolitan_france_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.metropolitan_france_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.classified_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.classified_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.monument_prerequisite',
        content: t(
          `quotes.form.switch.${contract?.monument_prerequisite ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.liquidation_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.liquidation_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.wood_construction_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.wood_construction_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.form.wood_full_risk_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.wood_full_risk_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.near_industrial_activity_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.near_industrial_activity_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.waiver_clause_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.waiver_clause_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.roof_prerequisite',
        content: t(
          `quotes.form.switch.${contract?.roof_prerequisite ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.agricultural_building_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.agricultural_building_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.igh_igs_prerequisite',
        content: t(
          `quotes.form.switch.${contract?.igh_igs_prerequisite ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.peril_prerequisite',
        content: t(
          `quotes.form.switch.${contract?.peril_prerequisite ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.vacant_prequisite',
        content: t(
          `quotes.form.switch.${contract?.vacant_prequisite ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.demolition_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.demolition_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.zup_prerequisite',
        content: t(
          `quotes.form.switch.${contract?.zup_prerequisite ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.flooding_prerequisite',
        content: t(
          `quotes.form.switch.${contract?.flooding_prerequisite ? 'yes' : 'no'}`
        ),
        span: 3
      },
      {
        label: 'contracts.show.mriwk.condominium_surface_prerequisite',
        content: t(
          `quotes.form.switch.${
            contract?.condominium_surface_prerequisite ? 'yes' : 'no'
          }`
        ),
        span: 3
      },
      ...arrayConditionnalElement(
        [
          {
            label: 'contracts.show.mriwk.public_late_prerequisite',
            content: t(
              `quotes.form.switch.${
                contract?.public_late_prerequisite ? 'yes' : 'no'
              }`
            ),
            span: 3
          },
          {
            label: 'contracts.show.mriwk.is_asl_aful_prerequisite',
            content: t(
              `quotes.form.switch.${
                contract?.is_asl_aful_prerequisite ? 'yes' : 'no'
              }`
            ),
            span: 3
          },
          {
            label: 'contracts.show.mriwk.fast_food_prerequisite',
            content: t(
              `quotes.form.switch.${
                contract?.fast_food_prerequisite ? 'yes' : 'no'
              }`
            ),
            span: 3
          },
          {
            label: 'contracts.show.mriwk.temporary_activity_prerequisite',
            content: t(
              `quotes.form.switch.${
                contract?.temporary_activity_prerequisite ? 'yes' : 'no'
              }`
            ),
            span: 3
          },
          {
            label: 'contracts.show.mriwk.recreation_activity_prerequisite',
            content: t(
              `quotes.form.switch.${
                contract?.recreation_activity_prerequisite ? 'yes' : 'no'
              }`
            ),
            span: 3
          },
          {
            label: 'contracts.show.mriwk.political_activity_prerequisite',
            content: t(
              `quotes.form.switch.${
                contract?.political_activity_prerequisite ? 'yes' : 'no'
              }`
            ),
            span: 3
          },
          {
            label: 'contracts.show.mriwk.industrial_activity_prerequisite',
            content: t(
              `quotes.form.switch.${
                contract?.industrial_activity_prerequisite ? 'yes' : 'no'
              }`
            ),
            span: 3
          },
          {
            label: 'contracts.show.mriwk.transformed_products_prerequisite',
            content: t(
              `quotes.form.switch.${
                contract?.transformed_products_prerequisite ? 'yes' : 'no'
              }`
            ),
            span: 3
          },
          {
            label: 'contracts.show.mriwk.cold_room_prerequisite',
            content: t(
              `quotes.form.switch.${
                contract?.cold_room_prerequisite ? 'yes' : 'no'
              }`
            ),
            span: 3
          },
          {
            label: 'contracts.show.mriwk.unbuildable_prerequisite',
            content: t(
              `quotes.form.switch.${
                contract?.unbuildable_prerequisite ? 'yes' : 'no'
              }`
            ),
            span: 3
          }
        ],
        isWKMri({ building_type })
      )
    ]
  };

  const antecedentsInfo = {
    rcp: [
      {
        label: 'contracts.form.company_been_insured',
        content: t(`quotes.form.switch.${company_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(company_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.termination_reason_by_company',
        content:
          termination_reason_by_company &&
          t(`contracts.form.${termination_reason_by_company}`)
      },
      {
        label: 'contracts.form.claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div key={claim._id}>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    rcpwk: [
      {
        label: 'contracts.form.company_been_insured',
        content: t(`quotes.form.switch.${company_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(company_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.termination_reason_by_company',
        content:
          termination_reason_by_company &&
          t(`contracts.form.${termination_reason_by_company}`)
      },
      {
        label: 'contracts.form.claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div key={claim._id}>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    // rcphx: [],
    rcpcm: [
      {
        label: 'contracts.form.company_been_insured',
        content: t(`quotes.form.switch.${company_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(company_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.termination_reason_by_company',
        content:
          termination_reason_by_company &&
          t(`contracts.form.${termination_reason_by_company}`)
      },
      {
        label: 'contracts.form.claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div key={claim._id}>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    rcpml: [
      {
        label: 'contracts.form.company_been_insured',
        content: t(`quotes.form.switch.${company_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(company_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.termination_reason_by_company',
        content:
          termination_reason_by_company &&
          t(`contracts.form.${termination_reason_by_company}`)
      },
      {
        label: 'contracts.form.claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div key={claim._id}>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    association: [
      {
        label: t('contracts.form.association_been_insured'),
        content: t(`quotes.form.switch.${company_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(company_been_insured
        ? [
            {
              label: t('contracts.form.last_company_name_contract'),
              content: last_company_name
                ? `${last_company_name} | ${last_contract_number}`
                : 'Aucune donnée',
              span: 1
            },
            {
              label: t('contracts.form.last_termination_date'),
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : 'Aucune donnée',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        )
      },
      ...(termination_by_company
        ? [
            {
              label: 'contracts.form.termination_reason_by_company',
              content:
                termination_reason_by_company &&
                t(`contracts.form.${termination_reason_by_company}`)
            },
            {
              label: 'contracts.form.confirm_claims',
              key: 'confirm_claims',
              content: t(`quotes.form.switch.${confirm_claims ? 'yes' : 'no'}`)
            }
          ]
        : []),
      {
        label: 'contracts.form.number_claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div key={claim._id}>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    mrpwk: [
      {
        label: 'contracts.form.company_been_insured',
        content: t(`quotes.form.switch.${company_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(company_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.termination_reason_by_company',
        content:
          termination_reason_by_company &&
          t(`contracts.form.${termination_reason_by_company}`)
      },
      {
        label: 'contracts.form.claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    mrpcm: [
      {
        label: 'contracts.form.company_been_insured',
        content: t(`quotes.form.switch.${company_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(company_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.termination_reason_by_company',
        content:
          termination_reason_by_company &&
          t(`contracts.form.${termination_reason_by_company}`)
      },
      {
        label: 'contracts.form.claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    mrpml: [
      {
        label: 'contracts.form.company_been_insured',
        content: t(`quotes.form.switch.${company_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(company_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.termination_reason_by_company',
        content:
          termination_reason_by_company &&
          t(`contracts.form.${termination_reason_by_company}`)
      },
      {
        label: 'contracts.form.claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    mrp: [
      {
        label: 'contracts.form.company_been_insured',
        content: t(`quotes.form.switch.${company_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(company_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.termination_reason_by_company',
        content:
          termination_reason_by_company &&
          t(`contracts.form.${termination_reason_by_company}`)
      },
      {
        label: 'contracts.form.claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    mri: [
      {
        label: 'contracts.form.risk_been_insured',
        content: t(`quotes.form.switch.${risk_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(risk_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        ),
        span: 1
      },
      ...(termination_by_company
        ? [
            {
              label: 'contracts.form.termination_reason_by_company',
              content: termination_reason_by_company
                ? t(`contracts.form.${termination_reason_by_company}`)
                : 'Aucune donnée',
              span: 1
            },
            {
              label: 'contracts.form.termination_date_by_company',
              content: termination_date_by_company
                ? `${moment(termination_date_by_company).format('DD/MM/YYYY')}`
                : 'Aucune donnée',
              span: 1
            }
          ]
        : []),

      {
        label: 'contracts.form.number_claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    mriml: [
      {
        label: 'contracts.form.building_been_insured_last_36_months_no_gap',
        content: t(`quotes.form.switch.${risk_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(risk_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            },
            ...(surface_area >= 2000
              ? [
                  {
                    label:
                      'contracts.form.information_statements_submission_date',
                    content: t(
                      `quotes.form.switch.${
                        information_statements_submission_date ? 'yes' : 'no'
                      }`
                    ),
                    span: 1
                  }
                ]
              : [])
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        ),
        span: 1
      },
      ...(termination_by_company
        ? [
            {
              label: 'contracts.form.termination_reason_by_company',
              content: termination_reason_by_company
                ? t(`contracts.form.${termination_reason_by_company}`)
                : 'Aucune donnée',
              span: 1
            },
            {
              label: 'contracts.form.termination_date_by_company',
              content: termination_date_by_company
                ? `${moment(termination_date_by_company).format('DD/MM/YYYY')}`
                : 'Aucune donnée',
              span: 1
            }
          ]
        : []),

      {
        label: 'contracts.form.claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    pnoml: [
      {
        label: 'contracts.form.risk_been_insured',
        content: t(`quotes.form.switch.${risk_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(risk_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        ),
        span: 1
      },
      ...(termination_by_company
        ? [
            {
              label: 'contracts.form.termination_reason_by_company',
              content: termination_reason_by_company
                ? t(`contracts.form.${termination_reason_by_company}`)
                : 'Aucune donnée',
              span: 1
            },
            {
              label: 'contracts.form.termination_date_by_company',
              content: termination_date_by_company
                ? `${moment(termination_date_by_company).format('DD/MM/YYYY')}`
                : 'Aucune donnée',
              span: 1
            }
          ]
        : []),

      {
        label: 'contracts.form.claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ],
    mriwk: [
      {
        label: 'contracts.form.building_been_insured',
        content: t(`quotes.form.switch.${company_been_insured ? 'yes' : 'no'}`),
        span: 1
      },
      ...(company_been_insured
        ? [
            {
              label: 'contracts.form.last_company_name',
              content: last_company_name ? `${last_company_name}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_contract_number',
              content: last_contract_number ? `${last_contract_number}` : '-',
              span: 1
            },
            {
              label: 'contracts.form.last_termination_date',
              content: last_termination_date
                ? `${moment(last_termination_date).format('DD/MM/YYYY')}`
                : '-',
              span: 1
            }
          ]
        : []),
      {
        label: 'contracts.form.termination_by_company',
        content: t(
          `quotes.form.switch.${termination_by_company ? 'yes' : 'no'}`
        )
      },
      {
        label: 'contracts.form.termination_reason_by_company',
        content:
          termination_reason_by_company &&
          t(`contracts.form.${termination_reason_by_company}`)
      },
      {
        label: 'contracts.form.claims',
        content: claims && (
          <Popover
            title={t('contracts.form.claim_description')}
            placement="right"
            content={claims.map((claim) => {
              return (
                <div key={claim._id}>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_date')}: ${moment(
                      claim.claim_date
                    ).format('ll')}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_type')}: ${claim.claim_type}`}
                  </p>
                  <p style={{ fontSize: 10 }}>
                    {`${t('contracts.form.claim_amount')}: ${
                      claim.claim_amount
                    }`}
                  </p>
                  <Divider />
                </div>
              );
            })}
          >
            <Tag>{number_claims && number_claims}</Tag>
          </Popover>
        )
      }
    ]
  };

  const list = [
    {
      label: 'tickets.form.users',
      content:
        assignee && `${assignee.owner_first_name} ${assignee.owner_last_name}`,
      span: 3
    },
    {
      label: 'settings.tickets.form.voluntary_exemption',
      content:
        exemptions &&
        exemptions.find((element) => element === 'forcedExemption') &&
        exemptions.every((element) => element !== 'rcngawkExemption') ? (
          <Tag color={exemptionStatusColor.EXEMPTION}>
            {t('quotes.form.switch.yes')}
          </Tag>
        ) : (
          <p>{t('quotes.form.switch.no')}</p>
        ),
      span: 1
    },
    {
      label: 'settings.tickets.form.linked_document',
      content:
        file &&
        file.map((doc) => {
          return (
            <Popconfirm
              title={t('settings.messages.download-line-confirm')}
              okText={t('buttons.download')}
              okButtonProps={{ type: 'danger' }}
              cancelText={t('buttons.cancel')}
              onConfirm={() =>
                downloadDocument(
                  doc._id,
                  doc.metadata.originalName,
                  doc.metadata.type
                )
              }
              icon={<CheckOutlined />}
            >
              <Tag color="blue" style={{ cursor: 'pointer' }}>
                {doc.metadata.originalName}
                <DownloadOutlined
                  style={{ fontSize: 18, textAlign: 'center', marginLeft: 5 }}
                  type="download"
                />
              </Tag>
            </Popconfirm>
          );
        }),
      span: 2
    },
    {
      label: 'tickets.form.desired_premium',
      content: unknown_desired_cotisation
        ? t('global.to_define')
        : desired_premium
          ? `${desired_premium}€`
          : 'Aucune donnée',
      span: 1
    },
    {
      label: 'tickets.form.comment',
      content: exemptions && comment,
      span: 1
    },
    {
      label: 'tickets.form.web_site',
      content: web_site && (
        <a target="_blank" rel="noopener noreferrer" href={`${web_site}`}>
          {`${web_site}`}
        </a>
      ),
      span: 1
    },
    {
      label: 'settings.tickets.form.particular_clause',
      content: exemptions && particular_clause && particular_clause,
      span: 1
    },
    {
      label: 'tickets.form.status',
      content: ticketStatus && (
        <Tag color={tasksStatuses[ticketStatus]}>
          {t(`tickets.status.${ticketStatus}`).toUpperCase()}
        </Tag>
      ),
      span: 1
    },
    {
      label: 'tickets.form.priority',
      content: priority && (
        <Tag color={ticketsPrioritiesColor[priority]}>
          {t(`tickets.tags.${priority}`)}
        </Tag>
      ),
      span: 2
    },
    {
      label: 'tickets.form.status_modification',
      content: (ticketStatus === 'AWAITING_INFORMATION' ||
        ticketStatus === 'ACCEPTED') && (
        <>
          <Button
            style={{ marginTop: 20, backgroundColor: 'green' }}
            type="primary"
            onClick={() => setOpenModal(true)}
          >
            {ticketStatus === 'ACCEPTED'
              ? t('buttons.modify_project')
              : t('buttons.validate_modification')}
            <CheckOutlined />
          </Button>
          <ExemptionModificationModal
            ticketId={_id}
            openModal={openModal}
            setOpenModal={setOpenModal}
          />
        </>
      ),
      span: 3
    }
  ];

  return {
    costInfo: costsInfo[type],
    contractInfo: contractInfo[type],
    optionInfo: optionInfo[type],
    firstStepInfo: firstStepInfo[type],
    antecedentsInfo: antecedentsInfo[type],
    contributionInfo,
    bankInfo,
    contractStatusInfo,
    list
  };
};

export default useListContent;
